<template>
  <div>
    <v-card-text class="pt-0">
      <!-- Toggle button for showing other units -->
      <v-row dense class="mb-2">
        <v-col>
          <v-btn
            v-if="analysisMode === 'all-units'"
            small
            outlined
            :color="showOtherUnits ? 'primary' : 'grey'"
            @click="showOtherUnits = !showOtherUnits"
            class="mr-2"
          >
            <v-icon left small>{{
              showOtherUnits ? "mdi-eye-off" : "mdi-eye"
            }}</v-icon>
            {{
              showOtherUnits
                ? "Andere Einheiten ausblenden"
                : "Andere Einheiten anzeigen"
            }}
          </v-btn>
          <v-chip
            v-if="showOtherUnits && analysisMode === 'all-units'"
            small
            outlined
            color="info"
            class="ml-2"
          >
            {{ otherUnitsDisplay.length }} weitere Einheiten verfügbar
          </v-chip>

          <!-- Best-Effort Mode Info -->
          <!-- TODO: ADD Bar Chart -->
          <v-chip
            v-if="analysisMode === 'best-effort'"
            small
            outlined
            color="success"
            class="ml-2"
          >
            <v-icon left x-small>mdi-account-check</v-icon>
            {{ assignedPersonnelCount }} von
            {{ totalAvailablePersonnel }} Personen zugewiesen
          </v-chip>
          <v-chip
            v-if="
              analysisMode === 'best-effort' && externalAssignments.length > 0
            "
            small
            outlined
            color="orange"
            class="ml-2"
          >
            <v-icon left x-small>mdi-account-plus</v-icon>
            {{ externalAssignments.length }} externe Zuweisungen
          </v-chip>
        </v-col>
      </v-row>

      <v-row class="match-height" dense>
        <v-col>
          <div v-if="!unitsDisplay.length" class="mb-1">
            Keine Einsatzmittel zugewiesen
          </div>
          <div v-else class="">
            <!-- Best-Effort Mode -->
            <div v-if="analysisMode === 'best-effort'">
              <!-- Requested Units Section -->
              <div class="mb-4">
                <h4 class="subtitle-2 mb-2 grey--text d-flex align-center">
                  <v-icon small class="mr-1">mdi-chart-line</v-icon>
                  Best-Effort Zuweisungen ({{ unitsDisplay.length }} Einheiten)
                </h4>
                <v-row dense class="match-height">
                  <v-col
                    cols="12"
                    sm="12"
                    md="6"
                    lg="6"
                    v-for="(unit, index) in unitsDisplay"
                    :key="`best-effort-${index}`"
                  >
                    <UnitForecastCard
                      :unit="unit"
                      :show-suggestions="analysisMode === 'best-effort'"
                      @selectAlternative="handleAlternativeSelection"
                      @showAllAlternatives="handleShowAllAlternatives"
                      @suggestionAction="handleSuggestionAction"
                    />
                  </v-col>
                </v-row>
              </div>

              <!-- External Assignments Section -->
              <div v-if="externalAssignments.length > 0" class="mb-4">
                <h4 class="subtitle-2 mb-2 grey--text d-flex align-center">
                  <v-icon small class="mr-1" color="orange"
                    >mdi-account-plus</v-icon
                  >
                  Externe Zuweisungen ({{ externalAssignments.length }}
                  Einheiten)
                </h4>
                <v-row dense class="match-height">
                  <v-col
                    cols="12"
                    sm="12"
                    md="6"
                    lg="6"
                    v-for="(unit, index) in externalAssignments"
                    :key="`external-${index}`"
                  >
                    <UnitForecastCard
                      :unit="unit"
                      :is-other-unit="true"
                      :show-suggestions="analysisMode === 'best-effort'"
                      @selectAlternative="handleAlternativeSelection"
                      @showAllAlternatives="handleShowAllAlternatives"
                      @suggestionAction="handleSuggestionAction"
                    />
                  </v-col>
                </v-row>
              </div>

              <!-- Unassigned Personnel Summary -->
              <div
                v-if="
                  bestEffortAssignment &&
                  bestEffortAssignment.unassignedPersonnel.length > 0
                "
                class="mb-4"
              >
                <h4 class="subtitle-2 mb-2 grey--text">
                  Nicht zugewiesenes Personal ({{
                    bestEffortAssignment.unassignedPersonnel.length
                  }}
                  Personen)
                </h4>
                <v-alert type="info" outlined dense class="mb-2">
                  <div class="d-flex align-center">
                    <v-icon left>mdi-account-alert</v-icon>
                    <span
                      >Folgende Personen konnten keiner Position zugewiesen
                      werden:</span
                    >
                  </div>
                  <div class="mt-2">
                    <v-chip
                      v-for="person in bestEffortAssignment.unassignedPersonnel"
                      :key="person.user.uid"
                      small
                      outlined
                      class="mr-1 mb-1"
                    >
                      {{ person.user.displayName }}
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            right
                            x-small
                            v-bind="attrs"
                            v-on="on"
                            class="ml-1"
                          >
                            mdi-information
                          </v-icon>
                        </template>
                        <span
                          >Qualifikationen:
                          {{
                            person.user.fillablePositions.titles.join(", ")
                          }}</span
                        >
                      </v-tooltip>
                    </v-chip>
                  </div>
                </v-alert>
              </div>
            </div>

            <!-- Scenarios Mode -->
            <div v-else-if="analysisMode === 'scenarios'">
              <!-- Strategy Information Section -->
              <div class="mb-4">
                <h4 class="subtitle-2 mb-3 grey--text d-flex align-center">
                  <v-icon small class="mr-1">mdi-strategy</v-icon>
                  Besetzungsstrategien
                </h4>

                <v-row dense class="mb-4">
                  <v-col
                    cols="12"
                    sm="12"
                    md="4"
                    v-for="(strategyInfo, strategyKey) in getStrategyInfo()"
                    :key="strategyKey"
                  >
                    <v-card outlined class="strategy-info-card">
                      <v-card-title class="py-2 d-flex align-center">
                        <v-icon small :color="strategyInfo.color" class="mr-2">
                          {{ strategyInfo.icon }}
                        </v-icon>
                        <span class="subtitle-2">{{ strategyInfo.title }}</span>
                      </v-card-title>
                      <v-card-text class="pt-0 pb-2">
                        <div
                          class="text-caption strategy-description-truncated text--secondary"
                        >
                          {{ strategyInfo.description }}
                        </div>
                        <v-btn
                          text
                          outlined
                          x-small
                          :color="strategyInfo.color"
                          class="mt-2"
                          @click="showStrategyDetails(strategyKey)"
                        >
                          Mehr Details
                          <v-icon x-small class="ml-1"
                            >mdi-chevron-right</v-icon
                          >
                        </v-btn>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

              <!-- Scenario Selection -->
              <div class="mb-4">
                <h4 class="subtitle-2 mb-2 grey--text d-flex align-center">
                  <v-icon small class="mr-1">mdi-chart-multiple</v-icon>
                  Mögliche Besetzungen ({{ scenariosDisplay.length }}
                  Optionen)
                </h4>

                <!-- Scenario Cards -->
                <v-row dense class="match-height">
                  <v-col
                    cols="12"
                    sm="12"
                    md="6"
                    lg="4"
                    v-for="(scenario, index) in scenariosDisplay"
                    :key="`scenario-${index}`"
                  >
                    <ScenarioCard
                      :scenario="scenario"
                      :index="index"
                      :is-selected="selectedScenario === index"
                      :available-personnel="availablePersonnel"
                      @select="selectedScenario = index"
                    />
                  </v-col>
                </v-row>
              </div>

              <!-- Selected Scenario Details -->
              <div
                v-if="
                  selectedScenario !== null &&
                  scenariosDisplay[selectedScenario]
                "
                class="mb-4"
              >
                <h4 class="subtitle-2 mb-2 grey--text d-flex align-center">
                  <v-icon small class="mr-1">mdi-information</v-icon>
                  Szenario {{ selectedScenario + 1 }} - Details
                </h4>
                <v-row dense class="match-height">
                  <v-col
                    cols="12"
                    sm="12"
                    md="6"
                    lg="6"
                    v-for="(unit, index) in unitsDisplay"
                    :key="`scenario-unit-${index}`"
                  >
                    <UnitForecastCard
                      :unit="unit"
                      :show-suggestions="false"
                      @selectAlternative="handleAlternativeSelection"
                      @showAllAlternatives="handleShowAllAlternatives"
                      @suggestionAction="handleSuggestionAction"
                    />
                  </v-col>
                </v-row>
              </div>
            </div>

            <!-- All Units Mode -->
            <div v-else>
              <!-- Requested Units Section -->
              <div class="mb-4">
                <h4 class="subtitle-2 mb-2 grey--text">
                  Zugewiesene Einsatzmittel
                </h4>
                <v-row dense class="match-height">
                  <v-col
                    cols="12"
                    sm="12"
                    md="6"
                    lg="6"
                    v-for="(unit, index) in unitsDisplay"
                    :key="`requested-${index}`"
                  >
                    <UnitForecastCard
                      :unit="unit"
                      :show-suggestions="analysisMode === 'best-effort'"
                      @selectAlternative="handleAlternativeSelection"
                      @showAllAlternatives="handleShowAllAlternatives"
                      @suggestionAction="handleSuggestionAction"
                    />
                  </v-col>
                </v-row>
              </div>

              <!-- Other Units Section -->
              <div v-if="showOtherUnits && otherUnitsDisplay.length">
                <h4 class="subtitle-2 mb-2 grey--text">
                  Weitere verfügbare Einsatzmittel
                </h4>
                <v-row dense class="match-height">
                  <v-col
                    cols="12"
                    sm="12"
                    md="6"
                    lg="6"
                    v-for="(unit, index) in otherUnitsDisplay"
                    :key="`other-${index}`"
                  >
                    <UnitForecastCard
                      :unit="unit"
                      :is-other-unit="true"
                      :show-suggestions="analysisMode === 'best-effort'"
                      @selectAlternative="handleAlternativeSelection"
                      @showAllAlternatives="handleShowAllAlternatives"
                      @suggestionAction="handleSuggestionAction"
                    />
                  </v-col>
                </v-row>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Strategy Details Dialog -->
    <v-dialog v-model="strategyDetailsDialog" max-width="600px" persistent>
      <v-card v-if="getSelectedStrategyInfo()">
        <v-card-title class="d-flex align-center">
          <v-icon :color="getSelectedStrategyInfo().color" class="mr-2">
            {{ getSelectedStrategyInfo().icon }}
          </v-icon>
          <span>{{ getSelectedStrategyInfo().title }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeStrategyDetails">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="mb-3">
            <h4 class="subtitle-1 mb-2">Kurzbeschreibung</h4>
            <p class="body-2">
              {{ getSelectedStrategyInfo().description }}
            </p>
          </div>

          <v-divider class="mb-3"></v-divider>

          <div class="mb-3">
            <h4 class="subtitle-1 mb-2">Detaillierte Funktionsweise</h4>
            <p class="body-2">
              {{ getSelectedStrategyInfo().detailedDescription }}
            </p>
          </div>

          <v-alert
            :color="getSelectedStrategyInfo().color"
            outlined
            dense
            class="mt-3"
          >
            <div class="d-flex align-center">
              <v-icon
                small
                :color="getSelectedStrategyInfo().color"
                class="mr-2"
              >
                {{ getSelectedStrategyInfo().icon }}
              </v-icon>
              <span class="caption">
                Diese Strategie wird zur Generierung der entsprechenden
                Szenarien verwendet.
              </span>
            </div>
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeStrategyDetails"> Schließen </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { db } from "@/firebase";
import UnitForecastCard from "./UnitForecastCard.vue";
import ScenarioCard from "./ScenarioCard.vue";
export default {
  name: "scheduling-event-unit-forecast-card-content",
  props: {
    requestedUnits: {
      type: Array,
      required: true,
      default: () => [],
    },
    availablePersonnel: {
      type: Array,
      required: true,
    },
    small: {
      type: Boolean,
      default: false,
    },
    forecastMode: {
      type: String,
      default: "best-effort", // 'best-effort', 'all-units', or 'scenarios'
    },
  },
  components: {
    UnitForecastCard,
    ScenarioCard,
  },
  data() {
    return {
      units: [],
      onlyCountHighestFillablePosition: true,
      showOtherUnits: false,
      scenarios: [], // Store generated scenarios
      selectedScenario: null, // Currently selected scenario
      strategyDetailsDialog: false, // Strategy details dialog visibility
      selectedStrategyKey: null, // Currently selected strategy for details
    };
  },
  computed: {
    analysisMode() {
      return this.forecastMode;
    },
    totalAvailablePersonnel() {
      return this.availablePersonnel.filter(
        (person) => person.status && person.status.id === "1"
      ).length;
    },
    bestEffortAssignment() {
      if (this.analysisMode !== "best-effort") return null;
      return this.calculateBestEffortAssignment();
    },
    assignedPersonnelCount() {
      if (this.analysisMode !== "best-effort" || !this.bestEffortAssignment)
        return 0;
      return this.bestEffortAssignment.assignedPersonnel.size;
    },
    externalAssignments() {
      if (this.analysisMode !== "best-effort" || !this.bestEffortAssignment)
        return [];
      return this.bestEffortAssignment.externalAssignments;
    },
    scenariosDisplay() {
      if (this.analysisMode !== "scenarios") return [];
      return this.calculateScenarios();
    },
    unitsDisplay() {
      // for each item in "this.requestedUnits" -> find the matching unit in "this.units"; then for the correct matching unit look inside crewCombinations and return only the item with the id of the requestedCrewCombination
      if (!this.units.length) {
        return [];
      }

      if (this.analysisMode === "best-effort") {
        return this.getBestEffortUnitsDisplay();
      }

      if (this.analysisMode === "scenarios") {
        return this.getScenariosUnitsDisplay();
      }

      return this.getAllUnitsDisplay();
    },
    otherUnitsDisplay() {
      if (!this.units.length || !this.showOtherUnits) {
        return [];
      }

      // Get IDs of requested units to exclude them
      const requestedUnitIds = new Set(this.requestedUnits.map((ru) => ru.id));

      // Filter out requested units and units without crew combinations
      const otherUnits = this.units.filter(
        (unit) =>
          !requestedUnitIds.has(unit.meta.id) &&
          unit.crewCombinations &&
          unit.crewCombinations.length > 0
      );

      // Sort units by their priority (lower number = higher priority)
      const sortedUnits = otherUnits.sort((a, b) => {
        const priorityA = a.priority || 999; // Default to low priority if no priority set
        const priorityB = b.priority || 999;
        return priorityA - priorityB;
      });

      return sortedUnits.map((unitData) => {
        return {
          ...unitData,
          id: unitData.meta.id,
          crewCombinations: unitData.crewCombinations.map(
            (crewCombination) => ({
              ...crewCombination,
              fulfillmentStatus: this.checkFulfillmentStatus(
                unitData,
                crewCombination
              ),
              isSelected: false, // Other units are never "selected"
            })
          ),
        };
      });
    },
  },
  watch: {
    analysisMode(newMode) {
      // Reset selected scenario when switching modes
      if (newMode === "scenarios") {
        this.selectedScenario = null;
      }
    },
    scenariosDisplay(newScenarios) {
      // Auto-select first scenario when scenarios are available
      if (newScenarios.length > 0 && this.selectedScenario === null) {
        this.selectedScenario = 0;
      }
    },
  },
  created() {
    this.getRequestedUnits();
  },
  methods: {
    async getRequestedUnits() {
      try {
        // First, get all units from the organization
        const allUnitsSnapshot = await db
          .collection("organizations")
          .doc(this.$route.params.organizationId)
          .collection("units")
          .get();

        const allUnits = [];
        allUnitsSnapshot.forEach((doc) => {
          if (doc.exists) {
            allUnits.push({ id: doc.id, ...doc.data() });
          }
        });

        // Store all units for the "other units" feature
        this.units = allUnits;

        console.log(`Loaded ${allUnits.length} total units from organization`);
        console.log(`Requested units: ${this.requestedUnits.length}`);
      } catch (error) {
        console.error("Error fetching units:", error);
        return [];
      }
    },
    checkFulfillmentStatus(unit, crewCombination) {
      // Get required positions (non-optional only)
      const requiredPositions = crewCombination.positions.filter(
        (position) => !position.isOptional
      );

      // Get optional positions separately
      const optionalPositions = crewCombination.positions.filter(
        (position) => position.isOptional
      );
      let totalRequired = 0;
      let totalAvailable = 0;
      let fulfilledPositions = 0;
      const missingPositions = [];
      let positionAssignments = []; // Track assignments for summary

      if (this.onlyCountHighestFillablePosition) {
        // Use optimal assignment algorithm to find the best possible allocation
        const optimalResult = this.findOptimalAssignment(
          requiredPositions,
          optionalPositions
        );
        positionAssignments = optimalResult.assignments;
        fulfilledPositions = optimalResult.fulfilledCount;
        totalRequired = optimalResult.totalRequired;
        totalAvailable = optimalResult.totalAvailable;

        // Calculate missing positions (only for required positions)
        optimalResult.assignments.forEach((assignment) => {
          if (assignment.missing > 0 && !assignment.isOptional) {
            missingPositions.push(
              `${assignment.missing}x ${
                assignment.positionData.position.shortTitle ||
                assignment.positionData.position.title
              }`
            );
          }
        });
      } else {
        // Default: count all persons for each position (handle both required and optional)
        const allPositions = [...requiredPositions, ...optionalPositions];

        allPositions.forEach((position) => {
          const requiredCount = position.requiredCounter || 1;
          const isOptional = position.isOptional;

          // Only count required positions toward totalRequired
          if (!isOptional) {
            totalRequired += requiredCount;
          }

          const availablePersons = this.availablePersonnel.filter(
            (person) =>
              person.status &&
              person.status.id === "1" &&
              person.user.fillablePositions.ids.includes(position.position.id)
          );

          const availableCount = Math.min(
            availablePersons.length,
            requiredCount
          );
          totalAvailable += availableCount;

          // Enhanced logging for this position
          console.log(
            `--- Position: ${position.position.shortTitle} (Optional: ${isOptional}) ---`
          );
          console.log(
            `Required: ${requiredCount}, Available: ${availableCount}`
          );
          console.log(
            `Available Personnel:`,
            availablePersons.map((p) => p.user.displayName)
          );
          console.log(
            `Status: ${
              availableCount >= requiredCount
                ? "✓ FULFILLED"
                : "✗ NOT FULFILLED"
            }`
          );

          const isFulfilled = availableCount >= requiredCount;

          // Only count required positions toward fulfillment count
          if (isFulfilled && !isOptional) {
            fulfilledPositions++;
          }

          if (isFulfilled) {
            // Track assignment for summary (in default mode, we assign up to required count)
            const assignedPersons = availablePersons.slice(0, requiredCount);
            positionAssignments.push({
              position: position.position.title,
              positionData: position,
              status: "fulfilled",
              assigned: assignedPersons.map((p) => p.user.displayName),
              missing: 0,
              isOptional: isOptional,
            });
          } else {
            const missing = requiredCount - availableCount;
            if (!isOptional) {
              missingPositions.push(
                `${missing}x ${position.position.shortTitle}`
              );
            }
            console.log(`Missing: ${missing} person(s)`);
            // Track assignment for summary
            positionAssignments.push({
              position: position.position.title,
              positionData: position,
              status: "not_fulfilled",
              assigned: availablePersons.map((p) => p.user.displayName),
              missing: missing,
              isOptional: isOptional,
            });
          }
        });
      }

      // Determine status with more granular conditions
      let status;
      const fulfillmentPercentage =
        totalRequired > 0
          ? Math.round((totalAvailable / totalRequired) * 100)
          : 0;

      if (fulfilledPositions === requiredPositions.length) {
        status = "fulfilled";
      } else if (fulfilledPositions > 0 || fulfillmentPercentage >= 25) {
        // Only consider it partially fulfilled if we have some positions fulfilled
        // OR if we have at least 25% of required personnel available
        status = "partially_fulfilled";
      } else {
        status = "not_fulfilled";
      }

      // Final log summary
      console.log("=== FULFILLMENT CALCULATION SUMMARY ===");
      console.log(`Unit: ${unit.title}`);
      console.log(`Crew Combination: ${crewCombination.title}`);
      console.log(
        `Mode: ${
          this.onlyCountHighestFillablePosition
            ? "Optimal Assignment Algorithm"
            : "All Positions"
        }`
      );
      console.log(`Required Positions: ${requiredPositions.length}`);
      console.log(`Fulfilled Positions: ${fulfilledPositions}`);
      console.log(`Total Required Personnel: ${totalRequired}`);
      console.log(`Total Available Personnel: ${totalAvailable}`);
      console.log(`Final Status: ${status}`);

      // Show detailed position assignments
      console.log("\n--- POSITION ASSIGNMENTS ---");
      positionAssignments.forEach((assignment) => {
        const statusSymbol = assignment.status === "fulfilled" ? "✓" : "✗";
        console.log(`${statusSymbol} ${assignment.position}:`);
        if (assignment.assigned.length > 0) {
          console.log(`   Assigned: ${assignment.assigned.join(", ")}`);
        }
        if (assignment.missing > 0) {
          console.log(`   Missing: ${assignment.missing} person(s)`);
        }
      });

      if (missingPositions.length > 0) {
        console.log(`\nOverall Missing: ${missingPositions.join(", ")}`);
      }
      console.log("==========================================");

      // Generate improvement suggestions
      const suggestions = this.generateImprovementSuggestions(
        positionAssignments,
        unit,
        crewCombination
      );

      // Calculate assignment reasoning
      const assignmentReasoning =
        this.generateAssignmentReasoning(positionAssignments);

      return {
        status,
        missingText: missingPositions.join(", "),
        totalRequired,
        totalAvailable,
        fulfilledPositions,
        positionDetails: positionAssignments,
        suggestions,
        assignmentReasoning,
        fulfillmentPercentage,
      };
    },
    findOptimalAssignment(requiredPositions, optionalPositions = []) {
      // Sort required positions by priority ascending (1 is highest)
      const sortedRequiredPositions = requiredPositions
        .slice()
        .sort((a, b) => a.position.priority - b.position.priority);

      // Sort optional positions by priority ascending (1 is highest)
      const sortedOptionalPositions = optionalPositions
        .slice()
        .sort((a, b) => a.position.priority - b.position.priority);

      // Get all available personnel
      const availablePersonnel = this.availablePersonnel.filter(
        (person) => person.status && person.status.id === "1"
      );

      console.log("=== OPTIMAL ASSIGNMENT ALGORITHM ===");
      console.log(
        `Available Personnel: ${availablePersonnel
          .map((p) => p.user.displayName)
          .join(", ")}`
      );

      // First assign required positions, then try to fill optional positions with remaining personnel
      const bestRequiredAssignment = this.findBestAssignmentRecursive(
        sortedRequiredPositions,
        availablePersonnel,
        {},
        0
      );

      // Get personnel already assigned to required positions
      const assignedPersonnelIds = new Set();
      Object.values(bestRequiredAssignment).forEach((assignedList) => {
        assignedList.forEach((person) =>
          assignedPersonnelIds.add(person.user.uid)
        );
      });

      // Get remaining personnel for optional positions
      const remainingPersonnel = availablePersonnel.filter(
        (person) => !assignedPersonnelIds.has(person.user.uid)
      );

      // Try to assign optional positions with remaining personnel
      const bestOptionalAssignment = this.findBestAssignmentRecursive(
        sortedOptionalPositions,
        remainingPersonnel,
        {},
        0
      );

      // Combine required and optional assignments
      const combinedAssignment = {
        ...bestRequiredAssignment,
        ...bestOptionalAssignment,
      };
      const allPositions = [
        ...sortedRequiredPositions,
        ...sortedOptionalPositions,
      ];

      let totalRequired = 0;
      let totalAvailable = 0;
      let fulfilledCount = 0;
      const assignments = [];

      allPositions.forEach((position) => {
        const requiredCount = position.requiredCounter || 1;
        const isOptional = position.isOptional;

        // Only count required positions toward totalRequired
        if (!isOptional) {
          totalRequired += requiredCount;
        }

        const assignedPersons = combinedAssignment[position.position.id] || [];
        const availableCount = assignedPersons.length;
        totalAvailable += availableCount;

        // Enhanced logging for this position
        console.log(
          `--- Position: ${position.position.title} (Priority: ${position.position.priority}, Optional: ${isOptional}) ---`
        );
        console.log(`Required: ${requiredCount}, Available: ${availableCount}`);
        console.log(
          `Assigned Personnel:`,
          assignedPersons.map((p) => p.user.displayName)
        );

        const isFulfilled = availableCount >= requiredCount;
        console.log(
          `Status: ${isFulfilled ? "✓ FULFILLED" : "✗ NOT FULFILLED"}`
        );

        // Only count required positions toward fulfillment count
        if (isFulfilled && !isOptional) {
          fulfilledCount++;
        }

        assignments.push({
          position: position.position.title,
          positionData: position,
          status: isFulfilled ? "fulfilled" : "not_fulfilled",
          assigned: assignedPersons.map((p) => p.user.displayName),
          missing: Math.max(0, requiredCount - availableCount),
          isOptional: isOptional,
        });

        if (!isFulfilled) {
          const missing = requiredCount - availableCount;
          console.log(`Missing: ${missing} person(s)`);
        }
      });

      console.log(
        `Optimal assignment found: ${fulfilledCount}/${sortedRequiredPositions.length} required positions fulfilled`
      );

      return {
        assignments,
        fulfilledCount,
        totalRequired,
        totalAvailable,
      };
    },
    findBestAssignmentRecursive(
      positions,
      availablePersonnel,
      currentAssignment,
      positionIndex
    ) {
      // Base case: we've assigned all positions
      if (positionIndex >= positions.length) {
        return { ...currentAssignment };
      }

      const position = positions[positionIndex];
      const requiredCount = position.requiredCounter || 1;
      const positionId = position.position.id;

      // Find all personnel who can fill this position
      const eligiblePersonnel = availablePersonnel.filter((person) =>
        person.user.fillablePositions.ids.includes(positionId)
      );

      // Get already assigned personnel
      const alreadyAssigned = new Set();
      Object.values(currentAssignment).forEach((assignedList) => {
        assignedList.forEach((person) => alreadyAssigned.add(person.user.uid));
      });

      // Filter out already assigned personnel
      const unassignedEligible = eligiblePersonnel.filter(
        (person) => !alreadyAssigned.has(person.user.uid)
      );

      let bestAssignment = null;
      let bestScore = -1;

      // Try all possible combinations of personnel for this position
      // Also try assigning fewer people (or none) to this position
      for (
        let assignCount = Math.min(requiredCount, unassignedEligible.length);
        assignCount >= 0;
        assignCount--
      ) {
        const assignmentCombinations =
          assignCount === 0
            ? [[]]
            : this.getCombinations(unassignedEligible, assignCount);

        for (const combination of assignmentCombinations) {
          const testAssignment = {
            ...currentAssignment,
            [positionId]: combination,
          };

          // Recursively try to assign remaining positions
          const recursiveResult = this.findBestAssignmentRecursive(
            positions,
            availablePersonnel,
            testAssignment,
            positionIndex + 1
          );

          // Calculate score for this assignment
          const score = this.calculateAssignmentScore(
            positions,
            recursiveResult
          );

          if (score > bestScore) {
            bestScore = score;
            bestAssignment = recursiveResult;
          }
        }
      }

      return bestAssignment || currentAssignment;
    },
    getCombinations(arr, size) {
      if (size === 0) return [[]];
      if (size > arr.length) return [];

      const combinations = [];

      function backtrack(start, current) {
        if (current.length === size) {
          combinations.push([...current]);
          return;
        }

        for (let i = start; i < arr.length; i++) {
          current.push(arr[i]);
          backtrack(i + 1, current);
          current.pop();
        }
      }

      backtrack(0, []);
      return combinations;
    },
    calculateAssignmentScore(positions, assignment) {
      let score = 0;
      let fulfilledPositions = 0;

      positions.forEach((position) => {
        const requiredCount = position.requiredCounter || 1;
        const assignedCount = (assignment[position.position.id] || []).length;

        if (assignedCount >= requiredCount) {
          fulfilledPositions++;
          // Bonus for fulfilling position completely
          score += 100;
          // Higher priority positions get higher score
          score += 10 - position.position.priority;
        } else {
          // Partial credit for partial fulfillment
          score += (assignedCount / requiredCount) * 50;
        }
      });

      // Big bonus for fulfilling all positions
      if (fulfilledPositions === positions.length) {
        score += 1000;
      }

      return score;
    },
    generateImprovementSuggestions(positionAssignments, unit, crewCombination) {
      const suggestions = [];
      const missingPositions = positionAssignments.filter((a) => a.missing > 0);

      if (missingPositions.length === 0) {
        suggestions.push({
          type: "success",
          text: "Vollständig besetzt - keine Verbesserungen erforderlich",
          icon: "mdi-check-circle",
        });
        return suggestions;
      }

      // Suggest external units for critical missing positions
      const criticalMissing = missingPositions.filter(
        (p) => p.positionData.position.priority <= 2
      );
      if (criticalMissing.length > 0) {
        suggestions.push({
          type: "warning",
          text: `Externe Unterstützung für ${criticalMissing.length} kritische Position(en) empfohlen`,
          icon: "mdi-account-plus",
          action: "request_external",
        });
      }

      // Suggest alternative crew combinations
      if (crewCombination && unit.crewCombinations.length > 1) {
        suggestions.push({
          type: "info",
          text: `${
            unit.crewCombinations.length - 1
          } alternative Besetzung(en) verfügbar`,
          icon: "mdi-swap-horizontal",
          action: "show_alternatives",
        });
      }

      // Suggest training if many positions unfilled
      const unfulfillmentRate =
        missingPositions.length / positionAssignments.length;
      if (unfulfillmentRate > 0.5) {
        suggestions.push({
          type: "info",
          text: "Schulungsmaßnahmen zur Qualifizierung empfohlen",
          icon: "mdi-school",
          action: "suggest_training",
        });
      }

      return suggestions;
    },
    generateAssignmentReasoning(positionAssignments) {
      const reasoning = [];

      positionAssignments.forEach((assignment) => {
        if (assignment.status === "fulfilled") {
          reasoning.push({
            position: assignment.position,
            reason: `Optimal zugewiesen basierend auf Priorität ${assignment.positionData.position.priority}`,
            type: "success",
          });
        } else {
          reasoning.push({
            position: assignment.position,
            reason: `Unbesetzt - ${assignment.missing} Person(en) fehlen`,
            type: "error",
          });
        }
      });

      return reasoning;
    },
    getAlternativeCrewCombinations(unit, excludeId) {
      if (!unit.crewCombinations) return [];

      return unit.crewCombinations
        .filter((cc) => cc.id !== excludeId)
        .map((crewCombination) => ({
          ...crewCombination,
          fulfillmentStatus: this.checkFulfillmentStatus(unit, crewCombination),
        }))
        .sort(
          (a, b) =>
            b.fulfillmentStatus.fulfillmentPercentage -
            a.fulfillmentStatus.fulfillmentPercentage
        );
    },
    handleAlternativeSelection(data) {
      console.log("Alternative crew combination selected:", data);
      // Emit event to parent component or handle selection logic
      this.$emit("alternativeSelected", data);
    },
    handleShowAllAlternatives(unit) {
      console.log("Show all alternatives for unit:", unit.title);
      // Show dialog or navigate to detailed view
      this.$emit("showAllAlternatives", unit);
    },
    handleSuggestionAction(data) {
      console.log("Suggestion action triggered:", data);
      const { action, unit, suggestion } = data;

      switch (action) {
        case "request_external":
          this.$emit("requestExternalSupport", { unit, suggestion });
          break;
        case "show_alternatives":
          this.handleShowAllAlternatives(unit);
          break;
        case "suggest_training":
          this.$emit("suggestTraining", { unit, suggestion });
          break;
        default:
          console.log("Unknown suggestion action:", action);
      }
    },
    getAllUnitsDisplay() {
      // Group requested units by unit ID to avoid duplicates
      const groupedRequestedUnits = this.requestedUnits.reduce(
        (acc, requestedUnit) => {
          if (!acc[requestedUnit.id]) {
            acc[requestedUnit.id] = [];
          }
          acc[requestedUnit.id].push(requestedUnit);
          return acc;
        },
        {}
      );

      // Process each unique unit
      return Object.keys(groupedRequestedUnits).map((unitId) => {
        const requestedUnitsForThisUnit = groupedRequestedUnits[unitId];
        const primaryRequestedUnit = requestedUnitsForThisUnit[0]; // Use first occurrence as primary

        const unitData = this.units.find((unit) => unit.meta.id === unitId);

        if (!unitData) {
          console.warn(`No unit data found for unit ID: ${unitId}`);
          return primaryRequestedUnit;
        }

        const selectedCrewCombinations = requestedUnitsForThisUnit.map(
          (ru) => ru.crewCombination.id
        );

        // If onlyCountHighestFillablePosition is true, assign each person to their highest-priority fillable position only
        let positions = [];
        if (selectedCrewCombinations.length > 0) {
          const firstSelectedCrewCombination = unitData.crewCombinations.find(
            (crewCombination) =>
              crewCombination.id === selectedCrewCombinations[0]
          );

          if (firstSelectedCrewCombination) {
            // Include all positions - both required and optional
            positions = firstSelectedCrewCombination.positions;

            if (this.onlyCountHighestFillablePosition) {
              // Sort positions by priority ascending (1 is highest)
              positions = positions
                .slice()
                .sort((a, b) => a.position.priority - b.position.priority);
              // Track assigned user IDs
              const assignedUserIds = new Set();
              positions = positions.map((position) => {
                // Find persons who can fill this position and are not already assigned
                const persons = this.availablePersonnel.filter(
                  (person) =>
                    person.status &&
                    person.status.id === "1" &&
                    person.user.fillablePositions.ids.includes(
                      position.position.id
                    ) &&
                    !assignedUserIds.has(person.user.uid)
                );
                console.log(assignedUserIds);
                // Mark these persons as assigned
                persons.forEach((person) =>
                  assignedUserIds.add(person.user.uid)
                );
                console.log("Position", position.position.title);
                console.log("PositionID", position.position.id);
                console.log("Available Counter", persons.length);
                console.log(
                  "Matching persons",
                  persons.map((person) => person.user.displayName)
                );
                return {
                  ...position,
                  availableCounter: persons.length,
                  requiredCounter: 1,
                };
              });
            } else {
              // Default: count all persons for each position
              positions = positions.map((position) => {
                const persons = this.availablePersonnel.filter(
                  (person) =>
                    person.status &&
                    person.status.id === "1" &&
                    person.user.fillablePositions.ids.includes(
                      position.position.id
                    )
                );
                console.log("Position", position.title);
                console.log("Available Counter", persons.length);
                console.log(
                  "Matching persons",
                  persons.map((person) => person.user.displayName)
                );
                return {
                  ...position,
                  availableCounter: persons.length,
                  requiredCounter: 1,
                };
              });
            }
          }
        }

        return {
          ...primaryRequestedUnit,
          ...unitData,
          crewCombinations: unitData.crewCombinations.map(
            (crewCombination) => ({
              ...crewCombination,
              fulfillmentStatus: this.checkFulfillmentStatus(
                unitData,
                crewCombination
              ),
              isSelected: selectedCrewCombinations.includes(crewCombination.id),
            })
          ),
          selectedCrewCombination: {
            ...unitData.crewCombinations.find(
              (cc) => cc.id === selectedCrewCombinations[0]
            ),
            positions,
          },
          selectedCrewCombinationIds: selectedCrewCombinations,
          alternativeCombinations: this.getAlternativeCrewCombinations(
            unitData,
            selectedCrewCombinations[0]
          ),
          unitCommander: unitData.commander || null,
        };
      });
    },
    getBestEffortUnitsDisplay() {
      if (!this.bestEffortAssignment) return [];
      return this.bestEffortAssignment.requestedUnits;
    },
    calculateBestEffortAssignment() {
      console.log("=== BEST-EFFORT ASSIGNMENT CALCULATION ===");

      const availablePersonnel = this.availablePersonnel.filter(
        (person) => person.status && person.status.id === "1"
      );

      const assignedPersonnel = new Set();
      const requestedUnits = [];
      const externalAssignments = [];

      // Step 1: Assign personnel to requested units with fallback strategy
      const groupedRequestedUnits = this.requestedUnits.reduce(
        (acc, requestedUnit) => {
          if (!acc[requestedUnit.id]) {
            acc[requestedUnit.id] = [];
          }
          acc[requestedUnit.id].push(requestedUnit);
          return acc;
        },
        {}
      );

      Object.keys(groupedRequestedUnits).forEach((unitId) => {
        const requestedUnitsForThisUnit = groupedRequestedUnits[unitId];
        const primaryRequestedUnit = requestedUnitsForThisUnit[0];
        const unitData = this.units.find((unit) => unit.meta.id === unitId);

        if (!unitData) return;

        const selectedCrewCombinations = requestedUnitsForThisUnit.map(
          (ru) => ru.crewCombination.id
        );

        // Try the selected crew combination first
        const selectedCrewCombination = unitData.crewCombinations.find(
          (crewCombination) =>
            crewCombination.id === selectedCrewCombinations[0]
        );

        let bestResult = null;
        let bestCombination = selectedCrewCombination;

        if (selectedCrewCombination) {
          const currentAvailable = availablePersonnel.filter(
            (person) => !assignedPersonnel.has(person.user.uid)
          );

          bestResult = this.performBestEffortAssignment(
            selectedCrewCombination,
            currentAvailable,
            assignedPersonnel,
            unitData,
            false, // isExternal
            false // assignOptionalPositions - Phase 1: required only
          );

          console.log(
            `Selected crew combination ${selectedCrewCombination.title} for unit ${unitData.title}: ${bestResult.fulfillmentStatus.fulfillmentPercentage}% fulfillment`
          );

          // If selected crew combination can't be fulfilled adequately, try alternatives
          if (bestResult.fulfillmentStatus.fulfillmentPercentage < 75) {
            console.log(
              `Selected crew combination has low fulfillment (${bestResult.fulfillmentStatus.fulfillmentPercentage}%), trying alternatives...`
            );

            let bestScore = bestResult.fulfillmentStatus.fulfillmentPercentage;
            unitData.crewCombinations.forEach((crewCombination) => {
              if (crewCombination.id === selectedCrewCombination.id) return; // Skip already tried

              const testResult = this.performBestEffortAssignment(
                crewCombination,
                currentAvailable,
                new Set(assignedPersonnel), // Don't modify global set yet
                unitData,
                false, // isExternal
                false // assignOptionalPositions - Phase 1: required only
              );

              const score = testResult.fulfillmentStatus.fulfillmentPercentage;
              console.log(
                `Alternative crew combination ${crewCombination.title}: ${score}% fulfillment`
              );

              if (score > bestScore) {
                bestScore = score;
                bestResult = testResult;
                bestCombination = crewCombination;
                console.log(
                  `Found better alternative: ${crewCombination.title} with ${score}% fulfillment`
                );
              }
            });
          }
        }

        if (bestResult) {
          // Calculate fulfillment status for ALL crew combinations to show as tabs
          const allCrewCombinationsWithStatus = unitData.crewCombinations.map(
            (crewCombination) => {
              const currentAvailable = availablePersonnel.filter(
                (person) => !assignedPersonnel.has(person.user.uid)
              );

              const fulfillmentResult = this.performBestEffortAssignment(
                crewCombination,
                currentAvailable,
                new Set(assignedPersonnel), // Don't modify global set
                unitData,
                false, // isExternal
                false // assignOptionalPositions - Phase 1: required only
              );

              return {
                ...crewCombination,
                fulfillmentStatus: fulfillmentResult.fulfillmentStatus,
                isSelected: crewCombination.id === bestCombination.id,
                isBestOption:
                  crewCombination.id === bestCombination.id &&
                  fulfillmentResult.fulfillmentStatus.status === "fulfilled",
                isOriginallyRequested:
                  crewCombination.id === selectedCrewCombination.id,
              };
            }
          );

          // Mark personnel as assigned globally
          bestResult.assignments.forEach((assignment) => {
            assignment.assignedPersonnel.forEach((person) => {
              assignedPersonnel.add(person.user.uid);
            });
          });

          requestedUnits.push({
            ...primaryRequestedUnit,
            ...unitData,
            crewCombinations: allCrewCombinationsWithStatus,
            bestEffortAssignment: bestResult,
            unitCommander: unitData.commander || null,
            selectedCrewCombinationId: selectedCrewCombination.id,
            bestCrewCombinationId: bestCombination.id,
          });

          console.log(
            `Unit ${unitData.title} assigned with crew combination ${bestCombination.title} (${bestResult.fulfillmentStatus.fulfillmentPercentage}% fulfillment)`
          );
        }
      });

      // Step 2: Try to fill other high-priority units with remaining personnel
      const unassignedPersonnel = availablePersonnel.filter(
        (person) => !assignedPersonnel.has(person.user.uid)
      );

      if (unassignedPersonnel.length > 0) {
        console.log(
          `Attempting to assign remaining ${unassignedPersonnel.length} personnel to external units...`
        );

        // Get other units that could potentially be filled
        const otherUnits = this.units.filter(
          (unit) =>
            !groupedRequestedUnits[unit.meta.id] &&
            unit.crewCombinations &&
            unit.crewCombinations.length > 0
        );

        // Sort other units by their highest priority positions
        const unitsWithPriority = otherUnits
          .map((unitData) => {
            let highestPriority = 10; // Lower number = higher priority
            unitData.crewCombinations.forEach((crewCombination) => {
              crewCombination.positions.forEach((position) => {
                if (
                  !position.isOptional &&
                  position.position.priority < highestPriority
                ) {
                  highestPriority = position.position.priority;
                }
              });
            });
            return { unitData, highestPriority };
          })
          .sort((a, b) => a.highestPriority - b.highestPriority);

        console.log(
          `Found ${unitsWithPriority.length} external units, sorted by priority`
        );

        // Try to fill units in priority order
        unitsWithPriority.forEach(({ unitData, highestPriority }) => {
          const currentUnassigned = availablePersonnel.filter(
            (person) => !assignedPersonnel.has(person.user.uid)
          );

          if (currentUnassigned.length === 0) return;

          console.log(
            `Evaluating external unit ${unitData.title} (highest priority: ${highestPriority})`
          );

          // Find the best crew combination for this unit with available personnel
          let bestCombination = null;
          let bestResult = null;
          let bestScore = 0;

          unitData.crewCombinations.forEach((crewCombination) => {
            const result = this.performBestEffortAssignment(
              crewCombination,
              currentUnassigned,
              new Set(assignedPersonnel), // Don't modify global set yet
              unitData,
              true, // isExternal
              false // assignOptionalPositions - Phase 1: required only
            );

            const score = result.fulfillmentStatus.fulfillmentPercentage;
            if (score > bestScore && score > 0) {
              bestScore = score;
              bestCombination = crewCombination;
              bestResult = result;
            }
          });

          // Only assign if we can fulfill at least 50% and have high priority positions
          if (
            bestCombination &&
            bestResult &&
            bestScore >= 50 &&
            highestPriority <= 3
          ) {
            console.log(
              `External unit ${unitData.title} assigned with ${bestScore}% fulfillment (crew combination: ${bestCombination.title})`
            );

            // Calculate fulfillment status for ALL crew combinations for external units too
            const allCrewCombinationsWithStatus = unitData.crewCombinations.map(
              (crewCombination) => {
                const fulfillmentResult = this.performBestEffortAssignment(
                  crewCombination,
                  currentUnassigned,
                  new Set(assignedPersonnel), // Don't modify global set
                  unitData,
                  true, // isExternal
                  false // assignOptionalPositions - Phase 1: required only
                );

                return {
                  ...crewCombination,
                  fulfillmentStatus: fulfillmentResult.fulfillmentStatus,
                  isSelected: crewCombination.id === bestCombination.id,
                  isBestOption:
                    crewCombination.id === bestCombination.id &&
                    fulfillmentResult.fulfillmentStatus.status === "fulfilled",
                  isOriginallyRequested: false, // External units aren't originally requested
                };
              }
            );

            externalAssignments.push({
              ...unitData,
              id: unitData.meta.id,
              isExternal: true,
              crewCombinations: allCrewCombinationsWithStatus,
              bestEffortAssignment: bestResult,
              unitCommander: unitData.commander || null,
              bestCrewCombinationId: bestCombination.id,
            });

            // Mark personnel as assigned
            bestResult.assignments.forEach((assignment) => {
              assignment.assignedPersonnel.forEach((person) => {
                assignedPersonnel.add(person.user.uid);
              });
            });
          } else {
            console.log(
              `External unit ${unitData.title} skipped (${bestScore}% fulfillment, priority ${highestPriority})`
            );
          }
        });
      }

      const finalUnassignedPersonnel = availablePersonnel.filter(
        (person) => !assignedPersonnel.has(person.user.uid)
      );

      console.log(
        `Best-effort assignment complete (Phase 1 - Required positions only):`
      );
      console.log(`- Requested units: ${requestedUnits.length}`);
      console.log(`- External assignments: ${externalAssignments.length}`);
      console.log(
        `- Assigned personnel: ${assignedPersonnel.size}/${availablePersonnel.length}`
      );
      console.log(`- Unassigned personnel: ${finalUnassignedPersonnel.length}`);

      // PHASE 2: Fill optional positions with remaining personnel
      console.log("=== PHASE 2: FILLING OPTIONAL POSITIONS ===");

      if (finalUnassignedPersonnel.length > 0) {
        console.log(
          `${finalUnassignedPersonnel.length} personnel available for optional positions`
        );

        // Collect all optional positions from all assigned units with their priorities
        const allOptionalPositions = [];

        // Process requested units
        requestedUnits.forEach((unit, unitIndex) => {
          const selectedCombination = unit.crewCombinations.find(
            (cc) => cc.isSelected
          );
          if (selectedCombination) {
            selectedCombination.positions?.forEach((position) => {
              if (position.isOptional) {
                allOptionalPositions.push({
                  unitIndex,
                  isExternal: false,
                  unitData: unit,
                  crewCombination: selectedCombination,
                  position,
                  priority: position.position.priority,
                });
              }
            });
          }
        });

        // Process external assignments
        externalAssignments.forEach((unit, unitIndex) => {
          const selectedCombination = unit.crewCombinations.find(
            (cc) => cc.isSelected
          );
          if (selectedCombination) {
            selectedCombination.positions?.forEach((position) => {
              if (position.isOptional) {
                allOptionalPositions.push({
                  unitIndex,
                  isExternal: true,
                  unitData: unit,
                  crewCombination: selectedCombination,
                  position,
                  priority: position.position.priority,
                });
              }
            });
          }
        });

        // Sort all optional positions by priority (1 is highest)
        allOptionalPositions.sort((a, b) => a.priority - b.priority);

        console.log(
          `Found ${allOptionalPositions.length} optional positions to fill`
        );

        // Fill optional positions in priority order
        allOptionalPositions.forEach((optionalPos) => {
          const currentAvailable = finalUnassignedPersonnel.filter(
            (person) => !assignedPersonnel.has(person.user.uid)
          );

          if (currentAvailable.length === 0) return;

          // Re-run assignment for this unit with optional positions enabled
          const updatedResult = this.performBestEffortAssignment(
            optionalPos.crewCombination,
            availablePersonnel.filter(
              (person) => !assignedPersonnel.has(person.user.uid)
            ),
            assignedPersonnel,
            optionalPos.unitData,
            optionalPos.isExternal,
            true // assignOptionalPositions - Phase 2: include optional
          );

          // Update the unit's crew combination with new assignments
          const targetUnits = optionalPos.isExternal
            ? externalAssignments
            : requestedUnits;
          const targetUnit = targetUnits[optionalPos.unitIndex];

          if (targetUnit) {
            // Update the selected crew combination with new fulfillment status
            const selectedCombination = targetUnit.crewCombinations.find(
              (cc) => cc.isSelected
            );
            if (selectedCombination) {
              selectedCombination.fulfillmentStatus =
                updatedResult.fulfillmentStatus;

              // Update global assigned personnel set with new assignments
              updatedResult.assignments.forEach((assignment) => {
                assignment.assignedPersonnel.forEach((person) => {
                  assignedPersonnel.add(person.user.uid);
                });
              });

              console.log(
                `Updated ${
                  optionalPos.isExternal ? "external" : "requested"
                } unit ${targetUnit.title} with optional position assignments`
              );
            }
          }
        });

        console.log("Phase 2 complete - Optional positions filled");
      }

      const finalUnassignedPersonnelAfterPhase2 = availablePersonnel.filter(
        (person) => !assignedPersonnel.has(person.user.uid)
      );

      console.log(`Final assignment summary:`);
      console.log(
        `- Total assigned personnel: ${assignedPersonnel.size}/${availablePersonnel.length}`
      );
      console.log(
        `- Final unassigned personnel: ${finalUnassignedPersonnelAfterPhase2.length}`
      );

      return {
        requestedUnits,
        externalAssignments,
        assignedPersonnel,
        unassignedPersonnel: finalUnassignedPersonnelAfterPhase2,
      };
    },
    performBestEffortAssignment(
      crewCombination,
      availablePersonnel,
      globalAssignedPersonnel,
      unitData,
      isExternal = false,
      assignOptionalPositions = false // New parameter to control optional position assignment
    ) {
      const requiredPositions = crewCombination.positions.filter(
        (position) => !position.isOptional
      );

      const optionalPositions = crewCombination.positions.filter(
        (position) => position.isOptional
      );

      // Sort required positions by priority (1 is highest)
      const sortedRequiredPositions = requiredPositions
        .slice()
        .sort((a, b) => a.position.priority - b.position.priority);

      // Sort optional positions by priority (1 is highest)
      const sortedOptionalPositions = optionalPositions
        .slice()
        .sort((a, b) => a.position.priority - b.position.priority);

      const assignments = [];
      const localAssignedPersonnel = new Set(globalAssignedPersonnel);
      let totalRequired = 0;
      let totalAssigned = 0;
      let fulfilledPositions = 0;

      // Always process required positions
      sortedRequiredPositions.forEach((position) => {
        const requiredCount = position.requiredCounter || 1;
        const isOptional = position.isOptional;

        // Only count required positions toward totalRequired
        if (!isOptional) {
          totalRequired += requiredCount;
        }

        // Find available personnel for this position
        const eligiblePersonnel = availablePersonnel.filter(
          (person) =>
            person.user.fillablePositions.ids.includes(position.position.id) &&
            !localAssignedPersonnel.has(person.user.uid)
        );

        const assignCount = Math.min(requiredCount, eligiblePersonnel.length);
        const assignedForThisPosition = eligiblePersonnel.slice(0, assignCount);

        // Mark as assigned
        assignedForThisPosition.forEach((person) => {
          localAssignedPersonnel.add(person.user.uid);
        });

        totalAssigned += assignCount;
        const isFulfilled = assignCount >= requiredCount;

        // Only count required positions toward fulfillment count
        if (isFulfilled && !isOptional) {
          fulfilledPositions++;
        }

        assignments.push({
          position: position.position.title,
          positionData: position,
          status: isFulfilled ? "fulfilled" : "not_fulfilled",
          assigned: assignedForThisPosition.map((p) => p.user.displayName),
          assignedPersonnel: assignedForThisPosition,
          missing: Math.max(0, requiredCount - assignCount),
          isOptional: isOptional,
        });
      });

      // Only process optional positions if explicitly requested (Phase 2)
      if (assignOptionalPositions) {
        sortedOptionalPositions.forEach((position) => {
          const requiredCount = position.requiredCounter || 1;
          const isOptional = position.isOptional;

          // Find available personnel for this position
          const eligiblePersonnel = availablePersonnel.filter(
            (person) =>
              person.user.fillablePositions.ids.includes(
                position.position.id
              ) && !localAssignedPersonnel.has(person.user.uid)
          );

          const assignCount = Math.min(requiredCount, eligiblePersonnel.length);
          const assignedForThisPosition = eligiblePersonnel.slice(
            0,
            assignCount
          );

          // Mark as assigned
          assignedForThisPosition.forEach((person) => {
            localAssignedPersonnel.add(person.user.uid);
          });

          // Note: Don't add to totalAssigned or totalRequired for optional positions
          const isFulfilled = assignCount >= requiredCount;

          assignments.push({
            position: position.position.title,
            positionData: position,
            status: isFulfilled ? "fulfilled" : "not_fulfilled",
            assigned: assignedForThisPosition.map((p) => p.user.displayName),
            assignedPersonnel: assignedForThisPosition,
            missing: Math.max(0, requiredCount - assignCount),
            isOptional: isOptional,
          });
        });
      } else {
        // Add placeholders for optional positions when not assigning them yet
        sortedOptionalPositions.forEach((position) => {
          assignments.push({
            position: position.position.title,
            positionData: position,
            status: "not_fulfilled",
            assigned: [],
            assignedPersonnel: [],
            missing: position.requiredCounter || 1,
            isOptional: position.isOptional,
          });
        });
      }

      const status =
        fulfilledPositions === sortedRequiredPositions.length
          ? "fulfilled"
          : fulfilledPositions > 0
          ? "partially_fulfilled"
          : "not_fulfilled";

      const fulfillmentPercentage =
        totalRequired > 0
          ? Math.round((totalAssigned / totalRequired) * 100)
          : 0;

      const missingPositions = [];
      assignments.forEach((assignment) => {
        if (assignment.missing > 0 && !assignment.isOptional) {
          missingPositions.push(
            `${assignment.missing}x ${
              assignment.positionData.position.shortTitle ||
              assignment.positionData.position.title
            }`
          );
        }
      });

      // Generate suggestions
      const suggestions = this.generateImprovementSuggestions(
        assignments,
        unitData,
        crewCombination
      );

      // Generate reasoning
      const assignmentReasoning = assignments.map((assignment) => ({
        position: assignment.position,
        reason:
          assignment.status === "fulfilled"
            ? `Optimal zugewiesen (${assignment.assigned.length}/${
                assignment.positionData.requiredCounter || 1
              })`
            : `Teilweise besetzt (${assignment.assigned.length}/${
                assignment.positionData.requiredCounter || 1
              })`,
        type: assignment.status === "fulfilled" ? "success" : "error",
      }));

      // Calculate remaining personnel after this assignment
      const remainingPersonnel = availablePersonnel.filter(
        (person) => !localAssignedPersonnel.has(person.user.uid)
      ).length;

      const fulfillmentStatus = {
        status,
        missingText: missingPositions.join(", "),
        totalRequired,
        totalAvailable: totalAssigned,
        fulfilledPositions,
        positionDetails: assignments,
        suggestions,
        assignmentReasoning,
        fulfillmentPercentage,
        remainingPersonnel,
      };

      return {
        assignments,
        fulfillmentStatus,
        isExternal,
      };
    },

    // New sophisticated assignment method following the requested logic
    performAdvancedScenarioAssignment(scenarioUnits, availablePersonnel) {
      console.log("=== STARTING ADVANCED SCENARIO ASSIGNMENT ===");

      const globalAssignedPersonnel = new Set();
      const evaluatedAssignments = [];
      let totalAssignedPersonnel = 0;

      // Step 1: Analyze all required positions across all units to determine priority conflicts
      const allRequiredPositions =
        this.analyzeAllRequiredPositions(scenarioUnits);

      // Step 2: Assign personnel starting with lowest priority required positions
      // ensuring we don't assign someone needed for higher priority positions later
      const assignmentPlan = this.createIntelligentAssignmentPlan(
        scenarioUnits,
        allRequiredPositions,
        availablePersonnel
      );

      // Step 3: Execute the assignment plan for required positions only
      scenarioUnits.forEach(({ unit, crewCombination }) => {
        const result = this.executeRequiredPositionAssignment(
          crewCombination,
          availablePersonnel,
          globalAssignedPersonnel,
          unit,
          assignmentPlan
        );

        evaluatedAssignments.push({
          unit,
          crewCombination,
          fulfillmentResult: result,
          isOriginallyRequested: true, // This will be set appropriately by calling method
        });
        totalAssignedPersonnel += result.fulfillmentStatus.totalAvailable;
      });

      // Step 4: Fill missing spots by looking at other units/combinations
      this.fillMissingSpotsWithCrossUnitOptimization(
        evaluatedAssignments,
        availablePersonnel,
        globalAssignedPersonnel
      );

      // Step 5: ONLY NOW assign optional positions at the very end
      this.assignOptionalPositionsAtEnd(
        evaluatedAssignments,
        availablePersonnel,
        globalAssignedPersonnel
      );

      return {
        assignedUnits: evaluatedAssignments,
        totalAssignedPersonnel: totalAssignedPersonnel,
        unassignedPersonnel: availablePersonnel.filter(
          (person) => !globalAssignedPersonnel.has(person.user.uid)
        ),
      };
    },

    // Analyze all required positions across units to understand priority conflicts
    analyzeAllRequiredPositions(scenarioUnits) {
      const positionAnalysis = new Map(); // positionId -> analysis data

      scenarioUnits.forEach(({ unit, crewCombination }) => {
        const requiredPositions = crewCombination.positions.filter(
          (pos) => !pos.isOptional
        );

        requiredPositions.forEach((position) => {
          const posId = position.position.id;
          const priority = position.position.priority || 999;

          if (!positionAnalysis.has(posId)) {
            positionAnalysis.set(posId, {
              positionId: posId,
              positionData: position.position,
              minPriority: priority,
              maxPriority: priority,
              demandByUnit: new Map(),
              totalDemand: 0,
            });
          }

          const analysis = positionAnalysis.get(posId);
          analysis.minPriority = Math.min(analysis.minPriority, priority);
          analysis.maxPriority = Math.max(analysis.maxPriority, priority);

          const unitId = unit.meta.id;
          const demand = position.requiredCounter || 1;
          analysis.demandByUnit.set(unitId, demand);
          analysis.totalDemand += demand;
        });
      });

      return positionAnalysis;
    },

    // Create intelligent assignment plan to avoid conflicts
    createIntelligentAssignmentPlan(
      scenarioUnits,
      allRequiredPositions,
      availablePersonnel
    ) {
      const assignmentPlan = new Map(); // unitId-positionId -> assignment strategy

      // Get available personnel count for each position
      const personnelByPosition = new Map();
      allRequiredPositions.forEach((analysis, positionId) => {
        const eligiblePersonnel = availablePersonnel.filter((person) =>
          person.user.fillablePositions.ids.includes(positionId)
        );
        personnelByPosition.set(positionId, eligiblePersonnel);
      });

      // Sort positions by priority (lowest number = highest priority)
      const sortedPositions = Array.from(allRequiredPositions.values()).sort(
        (a, b) => a.minPriority - b.minPriority
      );

      // For each position, determine assignment strategy
      sortedPositions.forEach((analysis) => {
        const availableCount = personnelByPosition.get(
          analysis.positionId
        ).length;
        const totalDemand = analysis.totalDemand;

        if (availableCount >= totalDemand) {
          // Sufficient personnel - can assign freely
          analysis.demandByUnit.forEach((demand, unitId) => {
            assignmentPlan.set(`${unitId}-${analysis.positionId}`, {
              strategy: "assign_freely",
              maxAssign: demand,
              priority: analysis.minPriority,
            });
          });
        } else {
          // Insufficient personnel - need to prioritize
          const unitsNeedingPosition = Array.from(
            analysis.demandByUnit.entries()
          );
          // Could add unit priority logic here - for now, use original order

          let remainingPersonnel = availableCount;
          unitsNeedingPosition.forEach(([unitId, demand]) => {
            const canAssign = Math.min(demand, remainingPersonnel);
            if (canAssign > 0) {
              assignmentPlan.set(`${unitId}-${analysis.positionId}`, {
                strategy: "assign_limited",
                maxAssign: canAssign,
                priority: analysis.minPriority,
              });
              remainingPersonnel -= canAssign;
            } else {
              assignmentPlan.set(`${unitId}-${analysis.positionId}`, {
                strategy: "skip",
                maxAssign: 0,
                priority: analysis.minPriority,
              });
            }
          });
        }
      });

      return assignmentPlan;
    },

    // Execute assignment for required positions only using the intelligent plan
    executeRequiredPositionAssignment(
      crewCombination,
      availablePersonnel,
      globalAssignedPersonnel,
      unit,
      assignmentPlan
    ) {
      const requiredPositions = crewCombination.positions.filter(
        (pos) => !pos.isOptional
      );
      const assignments = [];
      let totalRequired = 0;
      let totalAssigned = 0;
      let fulfilledPositions = 0;

      // Sort required positions by priority (lowest number = highest priority)
      const sortedPositions = requiredPositions
        .slice()
        .sort(
          (a, b) => (a.position.priority || 999) - (b.position.priority || 999)
        );

      sortedPositions.forEach((position) => {
        const requiredCount = position.requiredCounter || 1;
        totalRequired += requiredCount;

        const planKey = `${unit.meta.id}-${position.position.id}`;
        const plan = assignmentPlan.get(planKey);

        let assignedPersonnel = [];

        if (plan && plan.strategy !== "skip") {
          // Find eligible personnel who aren't already assigned
          const eligiblePersonnel = availablePersonnel.filter(
            (person) =>
              person.user.fillablePositions.ids.includes(
                position.position.id
              ) && !globalAssignedPersonnel.has(person.user.uid)
          );

          // Assign up to the planned maximum
          const maxToAssign = Math.min(
            plan.maxAssign,
            eligiblePersonnel.length
          );
          assignedPersonnel = eligiblePersonnel.slice(0, maxToAssign);

          // Update global assigned personnel
          assignedPersonnel.forEach((person) => {
            globalAssignedPersonnel.add(person.user.uid);
          });
        }

        const assigned = assignedPersonnel.length;
        totalAssigned += assigned;

        if (assigned >= requiredCount) {
          fulfilledPositions++;
        }

        assignments.push({
          positionData: position,
          requiredCount,
          assignedPersonnel,
          missing: Math.max(0, requiredCount - assigned),
          isFulfilled: assigned >= requiredCount,
          isOptional: false,
        });
      });

      const fulfillmentPercentage =
        totalRequired > 0 ? (totalAssigned / totalRequired) * 100 : 100;

      return {
        assignments,
        fulfillmentStatus: {
          totalRequired,
          totalAvailable: totalAssigned,
          fulfilledPositions,
          totalPositions: requiredPositions.length,
          fulfillmentPercentage,
          missingPersonnel: Math.max(0, totalRequired - totalAssigned),
        },
      };
    },

    // Fill missing spots by optimizing across units
    fillMissingSpotsWithCrossUnitOptimization(
      evaluatedAssignments,
      availablePersonnel,
      globalAssignedPersonnel
    ) {
      console.log("=== CROSS-UNIT OPTIMIZATION ===");

      // Find units with missing personnel
      const unitsWithMissing = evaluatedAssignments.filter((assignment) => {
        return assignment.fulfillmentResult.assignments.some(
          (pos) => pos.missing > 0
        );
      });

      if (unitsWithMissing.length === 0) return;

      // Get remaining personnel
      const remainingPersonnel = availablePersonnel.filter(
        (person) => !globalAssignedPersonnel.has(person.user.uid)
      );

      if (remainingPersonnel.length === 0) return;

      // Try to fill missing positions with remaining personnel
      unitsWithMissing.forEach((unitAssignment) => {
        unitAssignment.fulfillmentResult.assignments.forEach(
          (positionAssignment) => {
            if (
              positionAssignment.missing > 0 &&
              !positionAssignment.isOptional
            ) {
              const eligiblePersonnel = remainingPersonnel.filter(
                (person) =>
                  person.user.fillablePositions.ids.includes(
                    positionAssignment.positionData.position.id
                  ) && !globalAssignedPersonnel.has(person.user.uid)
              );

              const canAssign = Math.min(
                positionAssignment.missing,
                eligiblePersonnel.length
              );
              if (canAssign > 0) {
                const newlyAssigned = eligiblePersonnel.slice(0, canAssign);

                // Update assignment
                positionAssignment.assignedPersonnel.push(...newlyAssigned);
                positionAssignment.missing -= canAssign;
                positionAssignment.isFulfilled =
                  positionAssignment.missing === 0;

                // Update global tracking
                newlyAssigned.forEach((person) => {
                  globalAssignedPersonnel.add(person.user.uid);
                });

                // Update fulfillment status
                unitAssignment.fulfillmentResult.fulfillmentStatus.totalAvailable +=
                  canAssign;
                unitAssignment.fulfillmentResult.fulfillmentStatus.missingPersonnel -=
                  canAssign;

                if (positionAssignment.isFulfilled) {
                  unitAssignment.fulfillmentResult.fulfillmentStatus
                    .fulfilledPositions++;
                }

                // Recalculate percentage
                const totalRequired =
                  unitAssignment.fulfillmentResult.fulfillmentStatus
                    .totalRequired;
                const totalAvailable =
                  unitAssignment.fulfillmentResult.fulfillmentStatus
                    .totalAvailable;
                unitAssignment.fulfillmentResult.fulfillmentStatus.fulfillmentPercentage =
                  totalRequired > 0
                    ? (totalAvailable / totalRequired) * 100
                    : 100;
              }
            }
          }
        );
      });
    },

    // Assign optional positions only at the very end
    assignOptionalPositionsAtEnd(
      evaluatedAssignments,
      availablePersonnel,
      globalAssignedPersonnel
    ) {
      console.log("=== ASSIGNING OPTIONAL POSITIONS AT END ===");

      // Get remaining personnel
      const remainingPersonnel = availablePersonnel.filter(
        (person) => !globalAssignedPersonnel.has(person.user.uid)
      );

      if (remainingPersonnel.length === 0) {
        console.log("No remaining personnel for optional positions");
        return;
      }

      // For each unit, check if it has optional positions
      evaluatedAssignments.forEach((unitAssignment) => {
        const optionalPositions =
          unitAssignment.crewCombination.positions.filter(
            (pos) => pos.isOptional
          );

        if (optionalPositions.length === 0) return;

        // Sort optional positions by priority
        const sortedOptionalPositions = optionalPositions
          .slice()
          .sort(
            (a, b) =>
              (a.position.priority || 999) - (b.position.priority || 999)
          );

        sortedOptionalPositions.forEach((position) => {
          const requiredCount = position.requiredCounter || 1;

          const eligiblePersonnel = remainingPersonnel.filter(
            (person) =>
              person.user.fillablePositions.ids.includes(
                position.position.id
              ) && !globalAssignedPersonnel.has(person.user.uid)
          );

          const canAssign = Math.min(requiredCount, eligiblePersonnel.length);
          if (canAssign > 0) {
            const assignedPersonnel = eligiblePersonnel.slice(0, canAssign);

            // Add optional position assignment
            unitAssignment.fulfillmentResult.assignments.push({
              positionData: position,
              requiredCount,
              assignedPersonnel,
              missing: Math.max(0, requiredCount - canAssign),
              isFulfilled: canAssign >= requiredCount,
              isOptional: true,
            });

            // Update global tracking
            assignedPersonnel.forEach((person) => {
              globalAssignedPersonnel.add(person.user.uid);
            });

            // Update total positions count
            unitAssignment.fulfillmentResult.fulfillmentStatus.totalPositions++;
            if (canAssign >= requiredCount) {
              unitAssignment.fulfillmentResult.fulfillmentStatus
                .fulfilledPositions++;
            }
          }
        });
      });
    },

    // Wrapper method to use advanced assignment logic for individual scenarios
    performAdvancedSingleScenario(
      scenarioUnits,
      availablePersonnel,
      strategyName,
      strategyType
    ) {
      const result = this.performAdvancedScenarioAssignment(
        scenarioUnits,
        availablePersonnel
      );

      // Calculate total assigned personnel
      let totalAssignedPersonnel = 0;
      result.assignedUnits.forEach((assignment) => {
        totalAssignedPersonnel +=
          assignment.fulfillmentResult.fulfillmentStatus.totalAvailable;
      });

      return {
        assignedUnits: result.assignedUnits,
        totalAssignedPersonnel,
        strategy: strategyName,
        strategyType,
        strategyInfo: this.getStrategyInfo()[strategyType], // Add strategy metadata
        unassignedPersonnel: result.unassignedPersonnel,
      };
    },

    // Get strategy information with German titles and descriptions
    getStrategyInfo() {
      return {
        strategy1: {
          title: "Maximale Einheiten",
          shortTitle: "Max Einheiten",
          description:
            "Besetzt so viele Einheiten wie möglich. Beginnt mit Pflichtpositionen niedrigster Priorität und akzeptiert auch geringer priorisierte Besatzungskombinationen. Optionale Positionen werden nur am Ende besetzt.",
          detailedDescription:
            "Diese Strategie priorisiert die Anzahl der besetzten Einheiten über die vollständige Besetzung einzelner Einheiten. Sie beginnt systematisch mit den wichtigsten Pflichtpositionen (niedrigste Prioritätszahl) und stellt sicher, dass Personal nicht für höherpriorisierte Positionen blockiert wird. Auch weniger bevorzugte Besatzungskombinationen werden akzeptiert, um möglichst viele Einheiten einsatzbereit zu machen.",
          icon: "mdi-account-group",
          color: "primary",
        },
        strategy2: {
          title: "Optimale Personalausnutzung",
          shortTitle: "Effizienz",
          description:
            "Nutzt das verfügbare Personal so effizient wie möglich über alle Einheiten hinweg. Priorisiert Pflichtpositionen und optimiert einheitenübergreifend. Flexible Zuordnung für beste Gesamtabdeckung.",
          detailedDescription:
            "Diese Strategie fokussiert sich auf die bestmögliche Ausnutzung aller verfügbaren Personalressourcen. Sie analysiert alle möglichen Einheiten-Besatzungskombinationen und wählt iterativ die effizienteste Zuordnung. Dabei werden 80% Gewichtung auf Pflichtpositionen und 20% auf Personalausnutzung gelegt. Die Optimierung erfolgt global über alle Einheiten.",
          icon: "mdi-chart-line",
          color: "success",
        },
        strategy3: {
          title: "Externe Verstärkung",
          shortTitle: "Extern Ergänzt",
          description:
            "Besetzt angeforderte Einheiten wie spezifiziert und ergänzt verbleibendes Personal zu externen Einheiten. Fokus auf Kernbesetzung mit externer Unterstützung.",
          detailedDescription:
            "Diese Strategie erfüllt zunächst exakt die angeforderten Einheiten gemäß Spezifikation und nutzt dann das verbleibende Personal für externe Einheiten. Sie eignet sich besonders für Szenarien, wo bestimmte Kerneinheiten fest besetzt sein müssen und zusätzliche externe Unterstützung erwünscht ist.",
          icon: "mdi-account-plus",
          color: "warning",
        },
      };
    },

    // Get strategy title for display
    getStrategyTitle(strategyType) {
      const strategyInfo = this.getStrategyInfo();
      switch (strategyType) {
        case "strategy1":
          return strategyInfo.strategy1.title;
        case "strategy2":
          return strategyInfo.strategy2.title;
        case "strategy3":
          return strategyInfo.strategy3.title;
        default:
          return "Unbekannte Strategie";
      }
    },

    // Get strategy description for display
    getStrategyDescription(strategyType, detailed = false) {
      const strategyInfo = this.getStrategyInfo();
      const key = detailed ? "detailedDescription" : "description";
      switch (strategyType) {
        case "strategy1":
          return strategyInfo.strategy1[key];
        case "strategy2":
          return strategyInfo.strategy2[key];
        case "strategy3":
          return strategyInfo.strategy3[key];
        default:
          return "Keine Beschreibung verfügbar";
      }
    },

    // Scenarios Mode Methods
    calculateScenarios() {
      console.log("=== CALCULATING SCENARIOS WITH DIFFERENT STRATEGIES ===");

      try {
        const availablePersonnel = this.availablePersonnel.filter(
          (person) => person.status && person.status.id === "1"
        );

        console.log(`Available personnel: ${availablePersonnel.length}`);
        console.log(`Requested units: ${this.requestedUnits.length}`);

        if (availablePersonnel.length === 0) {
          console.warn("No available personnel found");
          return [];
        }

        const scenarios = [];

        // Strategy 1: Fulfill selected units by priority, then fill other internal units
        const strategy1Scenarios =
          this.generateStrategy1Scenarios(availablePersonnel);
        scenarios.push(...strategy1Scenarios);

        // Strategy 2: Allow flexible crew combinations for internal units
        const strategy2Scenarios =
          this.generateStrategy2Scenarios(availablePersonnel);
        scenarios.push(...strategy2Scenarios);

        // Strategy 3: Fulfill selected units, then fill external units
        const strategy3Scenarios =
          this.generateStrategy3Scenarios(availablePersonnel);
        scenarios.push(...strategy3Scenarios);

        console.log(
          `Generated ${scenarios.length} scenarios across all strategies`
        );

        // Score and sort scenarios
        const scoredScenarios = scenarios
          .map((scenario) => ({
            ...scenario,
            score: this.calculateScenarioScore(scenario, availablePersonnel),
          }))
          .sort((a, b) => b.score - a.score);

        // Remove duplicates based on unit assignments
        const uniqueScenarios = this.removeDuplicateScenarios(scoredScenarios);

        console.log(`Final unique scenarios: ${uniqueScenarios.length}`);

        // Return top scenarios (limit to prevent performance issues)
        return uniqueScenarios.slice(0, 8);
      } catch (error) {
        console.error("Error calculating scenarios:", error);
        return [];
      }
    },

    // Strategy 1: Maximale Einheiten - Besetzt so viele Einheiten wie möglich
    generateStrategy1Scenarios(availablePersonnel) {
      const strategyInfo = this.getStrategyInfo().strategy1;
      console.log(`=== STRATEGIE 1: ${strategyInfo.title.toUpperCase()} ===`);
      console.log(`Beschreibung: ${strategyInfo.description}`);

      const scenarios = [];
      const requestedUnitData = this.getRequestedUnitData();
      const otherInternalUnits = this.getOtherInternalUnits();

      // For each requested unit, try their crew combinations in priority order
      const requestedCombinations =
        this.getRequestedUnitCombinations(requestedUnitData);

      requestedCombinations.forEach((combination, index) => {
        const scenario = this.buildStrategy1Scenario(
          combination,
          otherInternalUnits,
          availablePersonnel,
          `Strategie 1.${index + 1}: ${strategyInfo.shortTitle}`
        );
        if (scenario.assignedUnits.length > 0) {
          scenarios.push(scenario);
        }
      });

      return scenarios;
    },

    // Strategy 2: Optimale Personalausnutzung - Nutzt Personal so effizient wie möglich
    generateStrategy2Scenarios(availablePersonnel) {
      const strategyInfo = this.getStrategyInfo().strategy2;
      console.log(`=== STRATEGIE 2: ${strategyInfo.title.toUpperCase()} ===`);
      console.log(`Beschreibung: ${strategyInfo.description}`);

      const scenarios = [];
      const requestedUnitData = this.getRequestedUnitData();
      const otherInternalUnits = this.getOtherInternalUnits();

      // Try different combinations where lower priority crew combinations can fill other units
      const flexibleCombinations = this.getFlexibleCombinations(
        requestedUnitData,
        otherInternalUnits
      );

      flexibleCombinations.slice(0, 3).forEach((combination, index) => {
        const scenario = this.buildStrategy2Scenario(
          combination,
          availablePersonnel,
          `Strategie 2.${index + 1}: ${strategyInfo.shortTitle}`
        );
        if (scenario.assignedUnits.length > 0) {
          scenarios.push(scenario);
        }
      });

      return scenarios;
    },

    // Strategy 3: Externe Verstärkung - Angeforderte Einheiten + externe Unterstützung
    generateStrategy3Scenarios(availablePersonnel) {
      const strategyInfo = this.getStrategyInfo().strategy3;
      console.log(`=== STRATEGIE 3: ${strategyInfo.title.toUpperCase()} ===`);
      console.log(`Beschreibung: ${strategyInfo.description}`);

      const scenarios = [];
      const requestedUnitData = this.getRequestedUnitData();
      const externalUnits = this.getExternalUnits();

      // For each requested unit combination, try to fill with external units
      const requestedCombinations =
        this.getRequestedUnitCombinations(requestedUnitData);

      requestedCombinations.slice(0, 2).forEach((combination, index) => {
        const scenario = this.buildStrategy3Scenario(
          combination,
          externalUnits,
          availablePersonnel,
          `Strategie 3.${index + 1}: ${strategyInfo.shortTitle}`
        );
        if (scenario.assignedUnits.length > 0) {
          scenarios.push(scenario);
        }
      });

      return scenarios;
    },

    generatePossibleAssignments() {
      const assignments = [];

      console.log("Generating possible assignments...");
      console.log(`Units available: ${this.units.length}`);
      console.log(`Requested units: ${this.requestedUnits.length}`);

      // For each requested unit, get all its crew combinations
      this.requestedUnits.forEach((requestedUnit) => {
        console.log(`Processing requested unit: ${requestedUnit.id}`);

        const unitData = this.units.find(
          (unit) => unit.meta.id === requestedUnit.id
        );

        if (!unitData) {
          console.warn(`Unit data not found for ID: ${requestedUnit.id}`);
          return;
        }

        if (
          !unitData.crewCombinations ||
          unitData.crewCombinations.length === 0
        ) {
          console.warn(
            `No crew combinations found for unit: ${unitData.title}`
          );
          return;
        }

        console.log(
          `Found ${unitData.crewCombinations.length} crew combinations for ${unitData.title}`
        );

        unitData.crewCombinations.forEach((crewCombination) => {
          assignments.push({
            unit: {
              ...unitData,
              id: unitData.meta.id,
              title: unitData.title,
              priority: unitData.priority || 999,
            },
            crewCombination: {
              ...crewCombination,
              id: crewCombination.id,
              title: crewCombination.title,
              positions: crewCombination.positions,
            },
            isOriginallyRequested:
              crewCombination.id === requestedUnit.crewCombination.id,
          });
        });
      });

      console.log(`Total possible assignments: ${assignments.length}`);
      return assignments;
    },

    generateScenariosCombinations(possibleAssignments, availablePersonnel) {
      const scenarios = [];

      // Group assignments by unit
      const assignmentsByUnit = {};
      possibleAssignments.forEach((assignment) => {
        if (!assignmentsByUnit[assignment.unit.id]) {
          assignmentsByUnit[assignment.unit.id] = [];
        }
        assignmentsByUnit[assignment.unit.id].push(assignment);
      });

      const unitIds = Object.keys(assignmentsByUnit);

      // Generate combinations (one crew combination per unit)
      const generateCombinations = (unitIndex, currentScenario) => {
        if (unitIndex >= unitIds.length) {
          // Complete scenario - evaluate it
          const scenario = this.evaluateScenario(
            currentScenario,
            availablePersonnel
          );
          if (scenario.assignedUnits.length > 0) {
            scenarios.push(scenario);
          }
          return;
        }

        const unitId = unitIds[unitIndex];
        const unitAssignments = assignmentsByUnit[unitId];

        // Try each crew combination for this unit
        unitAssignments.forEach((assignment) => {
          generateCombinations(unitIndex + 1, [...currentScenario, assignment]);
        });
      };

      generateCombinations(0, []);

      return scenarios;
    },

    // Helper methods for scenario strategies
    getRequestedUnitData() {
      return this.requestedUnits
        .map((requestedUnit) => {
          const unitData = this.units.find(
            (unit) => unit.meta.id === requestedUnit.id
          );
          return {
            ...requestedUnit,
            unitData,
            originalCrewCombination: requestedUnit.crewCombination,
          };
        })
        .filter((item) => item.unitData);
    },

    getOtherInternalUnits() {
      const requestedUnitIds = new Set(this.requestedUnits.map((ru) => ru.id));
      return this.units.filter(
        (unit) =>
          !requestedUnitIds.has(unit.meta.id) &&
          unit.crewCombinations &&
          unit.crewCombinations.length > 0 &&
          unit.priority <= 5 // Internal units have priority 5 or lower
      );
    },

    getExternalUnits() {
      const requestedUnitIds = new Set(this.requestedUnits.map((ru) => ru.id));
      return this.units.filter(
        (unit) =>
          !requestedUnitIds.has(unit.meta.id) &&
          unit.crewCombinations &&
          unit.crewCombinations.length > 0 &&
          (unit.priority > 5 || !unit.priority) // External units have priority > 5 or no priority
      );
    },

    getRequestedUnitCombinations(requestedUnitData) {
      const combinations = [];

      // Generate combinations of crew combinations for requested units
      const generateCombos = (unitIndex, currentCombo) => {
        if (unitIndex >= requestedUnitData.length) {
          combinations.push([...currentCombo]);
          return;
        }

        const unit = requestedUnitData[unitIndex];
        const crewCombinations = unit.unitData.crewCombinations
          .slice()
          .sort((a, b) => (a.priority || 999) - (b.priority || 999));

        // Try top 3 crew combinations for each unit to limit complexity
        crewCombinations.slice(0, 3).forEach((crewCombination) => {
          currentCombo.push({
            unit: unit.unitData,
            crewCombination,
            isOriginallyRequested:
              crewCombination.id === unit.originalCrewCombination.id,
          });
          generateCombos(unitIndex + 1, currentCombo);
          currentCombo.pop();
        });
      };

      generateCombos(0, []);

      // Sort combinations to prioritize originally requested ones
      return combinations
        .sort((a, b) => {
          const aOriginal = a.filter(
            (item) => item.isOriginallyRequested
          ).length;
          const bOriginal = b.filter(
            (item) => item.isOriginallyRequested
          ).length;
          return bOriginal - aOriginal;
        })
        .slice(0, 6); // Limit to top 6 combinations
    },

    getFlexibleCombinations(requestedUnitData, otherInternalUnits) {
      const combinations = [];
      const allInternalUnits = [
        ...requestedUnitData.map((item) => item.unitData),
        ...otherInternalUnits,
      ];

      // Generate flexible combinations where any unit can use any crew combination
      const generateFlexible = (unitIndex, currentCombo, usedUnits) => {
        if (unitIndex >= Math.min(allInternalUnits.length, 4)) {
          // Limit to 4 units max
          if (currentCombo.length > 0) {
            combinations.push([...currentCombo]);
          }
          return;
        }

        allInternalUnits.forEach((unit) => {
          if (usedUnits.has(unit.meta.id)) return;

          unit.crewCombinations.slice(0, 2).forEach((crewCombination) => {
            const isOriginallyRequested = requestedUnitData.some(
              (item) =>
                item.unitData.meta.id === unit.meta.id &&
                item.originalCrewCombination.id === crewCombination.id
            );

            currentCombo.push({
              unit,
              crewCombination,
              isOriginallyRequested,
            });
            usedUnits.add(unit.meta.id);
            generateFlexible(unitIndex + 1, currentCombo, usedUnits);
            usedUnits.delete(unit.meta.id);
            currentCombo.pop();
          });
        });
      };

      generateFlexible(0, [], new Set());

      return combinations.slice(0, 10); // Limit combinations
    },

    buildStrategy1Scenario(
      requestedCombination,
      otherInternalUnits,
      availablePersonnel,
      strategyName
    ) {
      const globalAssignedPersonnel = new Set();
      const evaluatedAssignments = [];
      let totalAssignedPersonnel = 0;

      // Strategy 1: Priority based internal focus with the goal of most units
      // Even accept lower prioritized crew combinations to maximize unit count

      // First, assign requested units by priority
      const sortedRequested = requestedCombination
        .slice()
        .sort(
          (a, b) =>
            (a.crewCombination.priority || 999) -
            (b.crewCombination.priority || 999)
        );

      sortedRequested.forEach((assignment) => {
        const result = this.performBestEffortAssignment(
          assignment.crewCombination,
          availablePersonnel,
          globalAssignedPersonnel,
          assignment.unit,
          false
        );

        this.updateGlobalAssignment(result, globalAssignedPersonnel);
        evaluatedAssignments.push({
          unit: assignment.unit,
          crewCombination: assignment.crewCombination,
          fulfillmentResult: result,
          isOriginallyRequested: assignment.isOriginallyRequested,
        });
        totalAssignedPersonnel += result.fulfillmentStatus.totalAvailable;
      });

      // Then aggressively try to fill ALL other internal units
      // Accept even low priority crew combinations to maximize unit count
      const sortedOtherUnits = otherInternalUnits
        .slice()
        .sort((a, b) => (a.priority || 999) - (b.priority || 999));

      sortedOtherUnits.forEach((unit) => {
        const remainingPersonnel = availablePersonnel.filter(
          (person) => !globalAssignedPersonnel.has(person.user.uid)
        );

        if (remainingPersonnel.length === 0) return;

        // Try ALL crew combinations for this unit, prioritizing higher priority ones
        // but accepting lower priority ones if they can be filled
        // Also consider skipping optional positions to fill non-optional ones
        const sortedCrewCombinations = unit.crewCombinations
          .slice()
          .sort((a, b) => (a.priority || 999) - (b.priority || 999));

        let bestResult = null;
        let bestCombination = null;

        sortedCrewCombinations.forEach((crewCombination) => {
          // Use optional skipping evaluation to prioritize non-optional positions
          const result = this.evaluateCrewCombinationOptionalSkipping(
            crewCombination,
            availablePersonnel,
            new Set(globalAssignedPersonnel), // Don't modify global set during testing
            unit
          );

          // Accept any crew combination that can be partially filled (even 1 person)
          // But prefer assignments that fill non-optional positions
          if (result.fulfillmentStatus.totalAvailable > 0) {
            const newScore = this.calculateOptionalSkippingScore(result);
            const currentScore = bestResult
              ? this.calculateOptionalSkippingScore(bestResult)
              : 0;

            if (newScore > currentScore) {
              bestResult = result;
              bestCombination = crewCombination;
            }
          }
        });

        if (bestResult && bestCombination) {
          const finalResult = this.performBestEffortAssignment(
            bestCombination,
            availablePersonnel,
            globalAssignedPersonnel,
            unit,
            false
          );

          this.updateGlobalAssignment(finalResult, globalAssignedPersonnel);
          evaluatedAssignments.push({
            unit,
            crewCombination: bestCombination,
            fulfillmentResult: finalResult,
            isOriginallyRequested: false,
          });
          totalAssignedPersonnel +=
            finalResult.fulfillmentStatus.totalAvailable;
        }
      });

      return {
        assignedUnits: evaluatedAssignments,
        totalAssignedPersonnel,
        strategy: strategyName,
        strategyType: "strategy1",
        unassignedPersonnel: availablePersonnel.filter(
          (person) => !globalAssignedPersonnel.has(person.user.uid)
        ),
      };
    },

    buildStrategy2Scenario(
      flexibleCombination,
      availablePersonnel,
      strategyName
    ) {
      const globalAssignedPersonnel = new Set();
      const evaluatedAssignments = [];
      let totalAssignedPersonnel = 0;

      // Strategy 2: Flexible crew combinations with the goal to assign all personnel in general
      // Focus on overall personnel utilization across the entire scenario
      // Prioritize non-optional positions and may skip optional ones to better utilize personnel

      // Get all available internal units (requested + others)
      const allInternalUnits = [
        ...new Set([
          ...flexibleCombination.map((item) => item.unit),
          ...this.getOtherInternalUnits(),
        ]),
      ];

      // Create a pool of all possible unit-crew combinations with scores
      const possibleAssignments = [];
      allInternalUnits.forEach((unit) => {
        unit.crewCombinations.forEach((crewCombination) => {
          possibleAssignments.push({
            unit,
            crewCombination,
            isOriginallyRequested: flexibleCombination.some(
              (item) => item.unit.meta.id === unit.meta.id
            ),
          });
        });
      });

      // Iteratively select the best assignment that utilizes remaining personnel efficiently
      let remainingPersonnel = [...availablePersonnel];
      let assignmentRound = 0;
      const maxRounds = allInternalUnits.length * 3; // Allow more rounds for thorough search

      while (remainingPersonnel.length > 0 && assignmentRound < maxRounds) {
        let bestAssignment = null;
        let bestResult = null;
        let bestEfficiency = 0;
        assignmentRound++;

        // Evaluate all possible assignments to find the most efficient one
        possibleAssignments.forEach(
          ({ unit, crewCombination, isOriginallyRequested }) => {
            // Skip if unit is already assigned
            if (
              evaluatedAssignments.some(
                (assignment) => assignment.unit.meta.id === unit.meta.id
              )
            ) {
              return;
            }

            if (remainingPersonnel.length === 0) return;

            // Evaluate with optional position skipping to prioritize non-optional roles
            const testResult = this.evaluateCrewCombinationOptionalSkipping(
              crewCombination,
              availablePersonnel,
              new Set(globalAssignedPersonnel),
              unit
            );

            if (testResult.fulfillmentStatus.totalAvailable === 0) return;

            // Calculate efficiency score focusing on personnel utilization rate
            const personnelUsed = testResult.fulfillmentStatus.totalAvailable;
            const nonOptionalFilled = testResult.assignments.filter(
              (a) => !a.isOptional && a.assignedPersonnel.length > 0
            ).length;
            const totalNonOptional = testResult.assignments.filter(
              (a) => !a.isOptional
            ).length;

            // Efficiency formula:
            // - Priority on filling non-optional positions (80% weight)
            // - Personnel utilization rate (20% weight)
            const nonOptionalRate =
              totalNonOptional > 0 ? nonOptionalFilled / totalNonOptional : 0;
            const personnelUtilizationRate =
              remainingPersonnel.length > 0
                ? personnelUsed / remainingPersonnel.length
                : 0;

            const efficiency =
              nonOptionalRate * 0.8 + personnelUtilizationRate * 0.2;

            if (efficiency > bestEfficiency) {
              bestEfficiency = efficiency;
              bestAssignment = { unit, crewCombination, isOriginallyRequested };
              bestResult = testResult;
            }
          }
        );

        // Apply the best assignment found
        if (bestAssignment && bestResult) {
          const finalResult = this.performBestEffortAssignment(
            bestAssignment.crewCombination,
            availablePersonnel,
            globalAssignedPersonnel,
            bestAssignment.unit,
            false
          );

          this.updateGlobalAssignment(finalResult, globalAssignedPersonnel);
          evaluatedAssignments.push({
            unit: bestAssignment.unit,
            crewCombination: bestAssignment.crewCombination,
            fulfillmentResult: finalResult,
            isOriginallyRequested: bestAssignment.isOriginallyRequested,
          });
          totalAssignedPersonnel +=
            finalResult.fulfillmentStatus.totalAvailable;

          // Update remaining personnel
          remainingPersonnel = availablePersonnel.filter(
            (person) => !globalAssignedPersonnel.has(person.user.uid)
          );
        } else {
          // No suitable assignment found, exit loop
          break;
        }
      }

      return {
        assignedUnits: evaluatedAssignments,
        totalAssignedPersonnel,
        strategy: strategyName,
        strategyType: "strategy2",
        unassignedPersonnel: remainingPersonnel,
      };
    },

    buildStrategy3Scenario(
      requestedCombination,
      externalUnits,
      availablePersonnel,
      strategyName
    ) {
      const globalAssignedPersonnel = new Set();
      const evaluatedAssignments = [];
      let totalAssignedPersonnel = 0;

      // Strategy 3: Requested units as specified, supplement remaining personnel to external units
      // First, assign requested units exactly as specified
      requestedCombination.forEach((assignment) => {
        const result = this.performBestEffortAssignment(
          assignment.crewCombination,
          availablePersonnel,
          globalAssignedPersonnel,
          assignment.unit,
          false
        );

        this.updateGlobalAssignment(result, globalAssignedPersonnel);
        evaluatedAssignments.push({
          unit: assignment.unit,
          crewCombination: assignment.crewCombination,
          fulfillmentResult: result,
          isOriginallyRequested: assignment.isOriginallyRequested,
        });
        totalAssignedPersonnel += result.fulfillmentStatus.totalAvailable;
      });

      // Then aggressively try to assign ALL remaining personnel to external units
      // Focus on utilizing as much remaining personnel as possible
      const sortedExternalUnits = externalUnits
        .slice()
        .sort((a, b) => (a.priority || 999) - (b.priority || 999));

      let remainingPersonnel = availablePersonnel.filter(
        (person) => !globalAssignedPersonnel.has(person.user.uid)
      );

      // Keep assigning external units until no more personnel can be assigned
      let assignmentRound = 0;
      const maxRounds = sortedExternalUnits.length * 2;

      while (remainingPersonnel.length > 0 && assignmentRound < maxRounds) {
        let assignedThisRound = false;
        assignmentRound++;

        sortedExternalUnits.forEach((unit) => {
          // Skip if unit is already assigned
          if (
            evaluatedAssignments.some(
              (assignment) => assignment.unit.meta.id === unit.meta.id
            )
          ) {
            return;
          }

          if (remainingPersonnel.length === 0) return;

          // Find the crew combination that can utilize the most remaining personnel
          // Also consider skipping optional positions to better utilize personnel for non-optional roles
          let bestCombination = null;
          let bestScore = 0;

          unit.crewCombinations.forEach((crewCombination) => {
            const testResult = this.evaluateCrewCombinationOptionalSkipping(
              crewCombination,
              availablePersonnel,
              new Set(globalAssignedPersonnel), // Don't modify global set during testing
              unit
            );

            const score = this.calculateOptionalSkippingScore(testResult);

            // Accept any crew combination that can use remaining personnel
            // Prioritize those that fill non-optional positions effectively
            if (score > bestScore) {
              bestScore = score;
              bestCombination = crewCombination;
            }
          });

          // Assign if we found a combination that uses at least 1 person
          if (bestCombination && bestScore > 0) {
            const finalResult = this.performBestEffortAssignment(
              bestCombination,
              availablePersonnel,
              globalAssignedPersonnel,
              unit,
              true
            );

            this.updateGlobalAssignment(finalResult, globalAssignedPersonnel);
            evaluatedAssignments.push({
              unit,
              crewCombination: bestCombination,
              fulfillmentResult: finalResult,
              isOriginallyRequested: false,
              isExternal: true,
            });
            totalAssignedPersonnel +=
              finalResult.fulfillmentStatus.totalAvailable;

            // Update remaining personnel
            remainingPersonnel = availablePersonnel.filter(
              (person) => !globalAssignedPersonnel.has(person.user.uid)
            );
            assignedThisRound = true;
          }
        });

        // If no assignments were made this round, break to avoid infinite loop
        if (!assignedThisRound) {
          break;
        }
      }

      return {
        assignedUnits: evaluatedAssignments,
        totalAssignedPersonnel,
        strategy: strategyName,
        strategyType: "strategy3",
        unassignedPersonnel: remainingPersonnel,
      };
    },

    findBestCrewCombinationForUnit(
      unit,
      availablePersonnel,
      globalAssignedPersonnel
    ) {
      let bestCombination = null;
      let bestScore = 0;

      unit.crewCombinations.forEach((crewCombination) => {
        const result = this.performBestEffortAssignment(
          crewCombination,
          availablePersonnel,
          new Set(globalAssignedPersonnel), // Don't modify the global set
          unit,
          false
        );

        const score = result.fulfillmentStatus.fulfillmentPercentage;
        if (score > bestScore) {
          bestScore = score;
          bestCombination = crewCombination;
        }
      });

      return bestCombination;
    },

    updateGlobalAssignment(result, globalAssignedPersonnel) {
      result.assignments.forEach((assignment) => {
        assignment.assignedPersonnel.forEach((person) => {
          globalAssignedPersonnel.add(person.user.uid);
        });
      });
    },

    removeDuplicateScenarios(scenarios) {
      const uniqueScenarios = [];
      const seenCombinations = new Set();

      scenarios.forEach((scenario) => {
        const signature = scenario.assignedUnits
          .map(
            (assignment) =>
              `${assignment.unit.meta.id}-${assignment.crewCombination.id}`
          )
          .sort()
          .join("|");

        if (!seenCombinations.has(signature)) {
          seenCombinations.add(signature);
          uniqueScenarios.push(scenario);
        }
      });

      return uniqueScenarios;
    },

    // Helper method to evaluate crew combination prioritizing non-optional positions
    evaluateCrewCombinationOptionalSkipping(
      crewCombination,
      availablePersonnel,
      globalAssignedPersonnel,
      unit
    ) {
      // Try assignment with all positions first
      const fullResult = this.performBestEffortAssignment(
        crewCombination,
        availablePersonnel,
        new Set(globalAssignedPersonnel),
        unit,
        false
      );

      // If all positions are fulfilled, use the full assignment
      if (fullResult.fulfillmentStatus.fulfillmentPercentage === 100) {
        return fullResult;
      }

      // Create a modified crew combination that skips optional positions if it helps
      const requiredPositions = crewCombination.positions.filter(
        (pos) => !pos.isOptional
      );
      const optionalPositions = crewCombination.positions.filter(
        (pos) => pos.isOptional
      );

      if (requiredPositions.length === 0) {
        // If all positions are optional, use full assignment
        return fullResult;
      }

      // Try assignment with only required positions
      const modifiedCrewCombination = {
        ...crewCombination,
        positions: requiredPositions,
      };

      const requiredOnlyResult = this.performBestEffortAssignment(
        modifiedCrewCombination,
        availablePersonnel,
        new Set(globalAssignedPersonnel),
        unit,
        false
      );

      // Calculate scores for comparison
      const fullScore = this.calculateOptionalSkippingScore(fullResult);
      const requiredOnlyScore =
        this.calculateOptionalSkippingScore(requiredOnlyResult);

      // Prefer the assignment that:
      // 1. Fills more non-optional positions
      // 2. Has a higher overall utilization of personnel for non-optional roles
      if (requiredOnlyScore > fullScore) {
        return {
          ...requiredOnlyResult,
          skippedOptionalPositions: optionalPositions.length,
        };
      }

      return fullResult;
    },

    // Calculate a score for an assignment prioritizing non-optional positions
    calculateOptionalSkippingScore(result) {
      let score = 0;
      let nonOptionalFulfilled = 0;
      let totalNonOptional = 0;

      result.assignments.forEach((assignment) => {
        if (!assignment.isOptional) {
          totalNonOptional += assignment.requiredCount;
          nonOptionalFulfilled += assignment.assignedPersonnel.length;
        }
      });

      // Primary score: percentage of non-optional positions filled
      const nonOptionalPercentage =
        totalNonOptional > 0
          ? (nonOptionalFulfilled / totalNonOptional) * 100
          : 0;
      score += nonOptionalPercentage * 10; // Weight non-optional positions heavily

      // Secondary score: total personnel utilized
      score += result.fulfillmentStatus.totalAvailable;

      return score;
    },

    // Enhanced method to find best crew combination considering optional position skipping
    findBestCrewCombinationWithOptionalSkipping(
      unit,
      availablePersonnel,
      globalAssignedPersonnel
    ) {
      let bestCombination = null;
      let bestResult = null;
      let bestScore = 0;

      unit.crewCombinations.forEach((crewCombination) => {
        const result = this.evaluateCrewCombinationOptionalSkipping(
          crewCombination,
          availablePersonnel,
          globalAssignedPersonnel,
          unit
        );

        const score = this.calculateOptionalSkippingScore(result);
        if (score > bestScore) {
          bestScore = score;
          bestCombination = crewCombination;
          bestResult = result;
        }
      });

      return { combination: bestCombination, result: bestResult };
    },

    evaluateScenario(assignments, availablePersonnel) {
      const globalAssignedPersonnel = new Set();
      const evaluatedAssignments = [];
      let totalAssignedPersonnel = 0;
      let totalFulfillmentPercentage = 0;

      assignments.forEach((assignment) => {
        const result = this.performBestEffortAssignment(
          assignment.crewCombination,
          availablePersonnel,
          globalAssignedPersonnel,
          assignment.unit,
          false
        );

        // Update global assigned personnel
        result.assignments.forEach((positionAssignment) => {
          positionAssignment.assignedPersonnel.forEach((person) => {
            globalAssignedPersonnel.add(person.user.uid);
          });
        });

        evaluatedAssignments.push({
          unit: assignment.unit,
          crewCombination: assignment.crewCombination,
          fulfillmentResult: result,
          isOriginallyRequested: assignment.isOriginallyRequested,
        });

        totalAssignedPersonnel += result.fulfillmentStatus.totalAvailable;
        totalFulfillmentPercentage +=
          result.fulfillmentStatus.fulfillmentPercentage;
      });

      return {
        assignedUnits: evaluatedAssignments,
        totalAssignedPersonnel,
        averageFulfillmentPercentage:
          assignments.length > 0
            ? totalFulfillmentPercentage / assignments.length
            : 0,
        unassignedPersonnel: availablePersonnel.filter(
          (person) => !globalAssignedPersonnel.has(person.user.uid)
        ),
      };
    },

    calculateScenarioScore(scenario, availablePersonnel) {
      let score = 0;
      let totalWeight = 0;

      scenario.assignedUnits.forEach((assignment) => {
        const fulfillmentPercentage =
          assignment.fulfillmentResult.fulfillmentStatus.fulfillmentPercentage;
        const unitPriority = assignment.unit.priority || 999;

        // Higher priority units (lower numbers) get more weight
        const weight = Math.max(1, 10 - unitPriority);

        // Bonus for originally requested crew combinations
        const originalBonus = assignment.isOriginallyRequested ? 10 : 0;

        score += (fulfillmentPercentage + originalBonus) * weight;
        totalWeight += weight;
      });

      // Calculate personnel utilization bonus
      const personnelUtilization =
        (scenario.totalAssignedPersonnel / availablePersonnel.length) * 100;
      const utilizationBonus = Math.min(personnelUtilization * 0.2, 20); // Max 20% bonus

      const finalScore =
        totalWeight > 0 ? score / totalWeight + utilizationBonus : 0;

      return Math.min(finalScore, 100); // Cap at 100%
    },

    getScenariosUnitsDisplay() {
      if (
        this.selectedScenario === null ||
        !this.scenariosDisplay[this.selectedScenario]
      ) {
        return [];
      }

      const scenario = this.scenariosDisplay[this.selectedScenario];
      return scenario.assignedUnits.map((assignment) => ({
        ...assignment.unit,
        crewCombinations: [
          {
            ...assignment.crewCombination,
            fulfillmentStatus: assignment.fulfillmentResult.fulfillmentStatus,
            isSelected: true,
          },
        ],
        selectedCrewCombination: assignment.crewCombination,
        selectedCrewCombinationIds: [assignment.crewCombination.id],
        alternativeCombinations: [],
        unitCommander: assignment.unit.commander || null,
        bestEffortAssignment: assignment.fulfillmentResult,
      }));
    },

    // Strategy UI methods
    showStrategyDetails(strategyKey) {
      this.selectedStrategyKey = strategyKey;
      this.strategyDetailsDialog = true;
    },

    closeStrategyDetails() {
      this.strategyDetailsDialog = false;
      this.selectedStrategyKey = null;
    },

    getSelectedStrategyInfo() {
      if (!this.selectedStrategyKey) return null;
      return this.getStrategyInfo()[this.selectedStrategyKey];
    },
  },
};
</script>

<style scoped>
.strategy-description {
  line-height: 1.4;
  max-height: 3.5em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
}

.strategy-description-truncated {
  line-height: 1.4;
  max-height: 2.8em; /* Approximately 2 lines */
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  position: relative;
}

.strategy-description-truncated::after {
  content: "";
  position: absolute;
  bottom: 0;
  right: 0;
  width: 30px;
  height: 1.4em;
  pointer-events: none;
}
</style>
